import React, { useState, useMemo, useContext, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FixedSizeList } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";

import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import BoltIcon from "@mui/icons-material/Bolt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewListIcon from "@mui/icons-material/ViewList";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import { useSelector } from "react-redux";

import { useGetAllGroupsQuery } from "../../redux/admin/groupApi";
import { useLazyGetCompactFleetDevicesQuery } from "../../redux/admin/deviceApi";
import { useUpdateAlarmRemarkMutation } from "../../redux/admin/alarmApi";
import { SocketContext } from "../../socket/SocketContext";
import { getCanonicalVehicleStatus, getResolvedLiveSnapshot } from "../../utils/vehicleStatus";
import { resolveDeviceListLikeStatus } from "../../utils/deviceListStatus";
import { resolveTrackingState } from "../../utils/trackingStateEngine";
import { useQuickModal } from "../../hooks/QuickModalContext";
import { useStatus } from "../../popup/NotificationContext";
import api from "../../redux/apis/api";
import { readDeviceViewCache, writeDeviceViewCache } from "../../utils/deviceViewCache";
import { resolveSidebarVehicleIcon } from "../../utils/sidebarVehicleIcon";

// ─── Constants ───────────────────────────────────────────────────────────────

const DRAWER_WIDTH = "clamp(360px, 24vw, 560px)";
const MOBILE_HEADER_OFFSET = 72;
const SIDEBAR_OBJECTS_PAGE_SIZE = 50;
const DEVICE_SIDEBAR_PAGE_CACHE_SCOPE = "device-sidebar-objects-page";

const STATUS_ORDER = ["all", "running", "idle", "stopped", "inactive", "expired", "no_data"];
const STATUS_PILL_STYLE = {
  all:      { bg: "#f97316", color: "#fff", label: "All" },
  running:  { bg: "#16a34a", color: "#fff", label: "Running" },
  idle:     { bg: "#ca8a04", color: "#fff", label: "Idle" },
  stopped:  { bg: "#dc2626", color: "#fff", label: "Stopped" },
  inactive: { bg: "#0ea5e9", color: "#fff", label: "No Reporting" },
  expired:  { bg: "#7c3aed", color: "#fff", label: "Expired" },
  no_data:  { bg: "#6b7280", color: "#fff", label: "No Data" },
};

const TABS = ["Objects"];
const SHARE_PRESETS = [
  { value: "1", label: "1 Hour", hours: 1 },
  { value: "8", label: "8 Hour", hours: 8 },
  { value: "24", label: "1 Day", hours: 24 },
  { value: "168", label: "7 Day", hours: 168 },
  { value: "custom", label: "Custom", hours: null },
];

const matchesDeviceSearch = (device, rawQuery) => {
  const query = safeLower(rawQuery).trim();
  if (!query) return true;

  const searchableValues = [
    device?.name,
    device?.vehicleNumber,
    device?.vehicleNo,
    device?.imei,
    device?.uniqueId,
    device?.deviceName,
    device?.simNo,
  ];

  return searchableValues.some((value) => safeLower(value).includes(query));
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const safeLower = (v) => String(v || "").toLowerCase();

const toTimestampMs = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value < 1e12 ? value * 1000 : value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    if (/^\d+$/.test(trimmed)) {
      const asNum = Number(trimmed);
      if (Number.isFinite(asNum)) return asNum < 1e12 ? asNum * 1000 : asNum;
    }
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseAccBool = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const l = value.trim().toLowerCase();
    if (["1","true","on","yes","acc_on","ignition_on","engine_on","engine_start","started"].includes(l)) return true;
    if (["0","false","off","no","acc_off","ignition_off","engine_off","engine_stop","stopped"].includes(l)) return false;
  }
  return null;
};

const parsePowerBool = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const l = value.trim().toLowerCase();
    if ([
      "1", "true", "on", "yes", "power_on", "external_power_on", "charging", "connected",
    ].includes(l)) return true;
    if ([
      "0", "false", "off", "no", "power_off", "power_cut", "external_power_cut", "external_power_off", "disconnected",
    ].includes(l)) return false;
  }
  return null;
};

const getBackendSidebarStatus = (device, liveInput = {}) => {
  return resolveDeviceListLikeStatus(device, liveInput);
};

const formatDate = (ts) => {
  if (!ts) return "";
  const d = new Date(toTimestampMs(ts));
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
  });
};

const formatTimeOnly = (ts) => {
  if (!ts) return "—";
  const d = new Date(toTimestampMs(ts));
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const toYMD = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildDateTimeFromParts = (date, hour, minute, meridiem) => {
  if (!date) return null;
  const h12 = Number(hour || 12);
  const mins = Number(minute || 0);
  let h24 = h12 % 12;
  if (String(meridiem || "AM").toUpperCase() === "PM") h24 += 12;
  const result = new Date(`${date}T00:00:00`);
  result.setHours(h24, mins, 0, 0);
  return result;
};

const formatDurationMins = (value) => {
  const totalMins = Math.max(0, Math.round(Number(value || 0)));
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")} min`;
};

const formatHistoryDistance = (value) => {
  const km = Number(value || 0);
  return `${km % 1 === 0 ? km.toFixed(0) : km.toFixed(2)} Kms`;
};

const extractHistoryAddress = (item, type) => {
  const candidates = type === "start"
    ? [
        item?.startAddress,
        item?.fromAddress,
        item?.originAddress,
        item?.pickupAddress,
        item?.startLocationAddress,
        item?.startLocationName,
        item?.startPlace,
        item?.startAddressText,
      ]
    : [
        item?.endAddress,
        item?.toAddress,
        item?.destinationAddress,
        item?.dropAddress,
        item?.endLocationAddress,
        item?.endLocationName,
        item?.endPlace,
        item?.endAddressText,
      ];
  return candidates.find((entry) => String(entry || "").trim()) || "NA";
};

const extractHistoryCoords = (item, type) => {
  const latCandidates = type === "start"
    ? [
        item?.startLat,
        item?.startLatitude,
        item?.fromLat,
        item?.fromLatitude,
        item?.startLocation?.lat,
        item?.startLocation?.latitude,
        item?.pickupLocation?.lat,
        item?.pickupLocation?.latitude,
      ]
    : [
        item?.endLat,
        item?.endLatitude,
        item?.toLat,
        item?.toLatitude,
        item?.endLocation?.lat,
        item?.endLocation?.latitude,
        item?.dropLocation?.lat,
        item?.dropLocation?.latitude,
      ];

  const lngCandidates = type === "start"
    ? [
        item?.startLng,
        item?.startLon,
        item?.startLongitude,
        item?.fromLng,
        item?.fromLon,
        item?.fromLongitude,
        item?.startLocation?.lng,
        item?.startLocation?.lon,
        item?.startLocation?.longitude,
        item?.pickupLocation?.lng,
        item?.pickupLocation?.longitude,
      ]
    : [
        item?.endLng,
        item?.endLon,
        item?.endLongitude,
        item?.toLng,
        item?.toLon,
        item?.toLongitude,
        item?.endLocation?.lng,
        item?.endLocation?.lon,
        item?.endLocation?.longitude,
        item?.dropLocation?.lng,
        item?.dropLocation?.longitude,
      ];

  const lat = latCandidates.map(Number).find((value) => Number.isFinite(value) && value !== 0);
  const lng = lngCandidates.map(Number).find((value) => Number.isFinite(value) && value !== 0);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const formatHistoryDateLabel = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getHistoryDeviceLabel = (device) =>
  device?.name || device?.vehicleNumber || device?.imei || "";

const historyPointDistanceKm = (from, to) => {
  const lat1 = Number(from?.lat);
  const lng1 = Number(from?.lng);
  const lat2 = Number(to?.lat);
  const lng2 = Number(to?.lng);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return 0;
  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return 6371 * c;
};

const getPlaybackPointAddress = (point) =>
  point?.resolvedAddress ||
  point?.geocodedAddress ||
  point?.address ||
  point?.locationAddress ||
  point?.currentAddress ||
  point?.displayAddress ||
  "NA";

const getPlaybackPointCoords = (point) => {
  const lat = Number(point?.lat ?? point?.latitude);
  const lng = Number(point?.lng ?? point?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const getPlaybackPointMode = (point) => {
  const explicit = safeLower(point?.vehicleStatus || point?.status).trim();
  const speed = Number(point?.speed || 0);
  const acc = parseAccBool(point?.accStatus ?? point?.acc ?? point?.ignition);
  if (explicit.includes("run") || explicit.includes("move")) return "ride";
  if (speed > 2) return "ride";
  if (explicit.includes("idle")) return "idle";
  if (acc === true) return "idle";
  return "park";
};

const buildHistoryRowsFromPlaybackPoints = (points = []) => {
  if (!Array.isArray(points) || !points.length) return [];

  const normalizedPoints = points
    .map((point) => {
      const timestampMs = toTimestampMs(
        point?.timestamp || point?.deviceTime || point?.serverTime || point?.createdAt
      );
      const coords = getPlaybackPointCoords(point);
      return {
        timestampMs,
        coords,
        address: getPlaybackPointAddress(point),
        mode: getPlaybackPointMode(point),
      };
    })
    .filter((point) => point.timestampMs)
    .sort((left, right) => left.timestampMs - right.timestampMs);

  if (!normalizedPoints.length) return [];

  const rows = [];
  let segment = null;

  const pushSegment = () => {
    if (!segment) return;
    const durationMinutes = Math.max(
      0,
      Math.round((segment.end.timestampMs - segment.start.timestampMs) / 60000)
    );
    rows.push({
      id: `${segment.start.timestampMs}-${segment.end.timestampMs}-${segment.mode}`,
      startTimeRaw: new Date(segment.start.timestampMs).toISOString(),
      endTimeRaw: new Date(segment.end.timestampMs).toISOString(),
      startLabel: formatHistoryDateLabel(segment.start.timestampMs),
      endLabel: formatHistoryDateLabel(segment.end.timestampMs),
      startAddress: segment.start.address || "NA",
      endAddress: segment.end.address || "NA",
      startCoords: segment.start.coords,
      endCoords: segment.end.coords,
      startAddressResolved: Boolean(segment.start.address && segment.start.address !== "NA"),
      endAddressResolved: Boolean(segment.end.address && segment.end.address !== "NA"),
      durationMinutes,
      durationText: formatDurationMins(durationMinutes),
      distanceText: formatHistoryDistance(Number(segment.distanceKm || 0)),
      isRide: segment.mode === "ride",
    });
  };

  normalizedPoints.forEach((point) => {
    if (!segment) {
      segment = { mode: point.mode, start: point, end: point, distanceKm: 0 };
      return;
    }

    if (segment.end.coords && point.coords) {
      segment.distanceKm += historyPointDistanceKm(segment.end.coords, point.coords);
    }

    if (segment.mode !== point.mode) {
      pushSegment();
      segment = { mode: point.mode, start: point, end: point, distanceKm: 0 };
      return;
    }

    segment.end = point;
  });

  pushSegment();
  return rows;
};

const prettyNotificationEvent = (value) => {
  if (!value) return "Unknown Event";
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getNotificationCoords = (alarm) => {
  const lat = Number(
    alarm?.location?.latitude ??
    alarm?.location?.lat ??
    alarm?.latitude ??
    alarm?.lat
  );
  const lng = Number(
    alarm?.location?.longitude ??
    alarm?.location?.lng ??
    alarm?.longitude ??
    alarm?.lng
  );
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const formatNotificationTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

// ─── IconCell (IGN / GPS / PWR column) ───────────────────────────────────────

const IconCell = ({ on, label, hidden = false, compact = false }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0, minWidth: compact ? 20 : 24, visibility: hidden ? "hidden" : "visible" }}>
    <Box
      sx={{
        fontSize: compact ? 11 : 12,
        color: on ? "#16a34a" : "#9ca3af",
        lineHeight: 1,
        transform: on ? "scale(1.08)" : "scale(1)",
        transition: "all 0.15s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      >
      {label === "IGN" ? (
        <DirectionsCarFilledOutlinedIcon sx={{ fontSize: compact ? 12 : 14, color: on ? "#84cc16" : "#d1d5db" }} />
      ) : label === "GPS" ? (
        <WifiIcon sx={{ fontSize: compact ? 12 : 14, color: on ? "#16a34a" : "#9ca3af" }} />
      ) : (
        <PowerSettingsNewIcon sx={{ fontSize: compact ? 12 : 14, color: on ? "#16a34a" : "#ef4444" }} />
      )}
    </Box>
    <Typography
      sx={{
        fontSize: compact ? "7px" : "8px",
        fontWeight: 800,
        color: on ? "text.primary" : "#111827",
        opacity: on ? 1 : 0.78,
        lineHeight: 1,
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const getTelemetryIconState = ({ status, ignOn, gpsOn, pwrOn }) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (["no_data", "nodata", "expired"].includes(normalizedStatus)) {
    return { hidden: true, ignOn: false, gpsOn: false, pwrOn: false };
  }

  if (normalizedStatus === "running") {
    return { hidden: false, ignOn: true, gpsOn: gpsOn, pwrOn: true };
  }

  if (normalizedStatus === "idle") {
    return { hidden: false, ignOn: true, gpsOn: gpsOn, pwrOn: pwrOn };
  }

  if (normalizedStatus === "stopped") {
    return { hidden: false, ignOn: false, gpsOn: gpsOn, pwrOn: pwrOn };
  }

  if (normalizedStatus === "inactive") {
    return { hidden: false, ignOn, gpsOn, pwrOn: false };
  }

  return { hidden: false, ignOn, gpsOn, pwrOn };
};

const getSidebarStatusBadgeTheme = (status) => {
  const palette = {
    running: { bg: "#dcfce7", color: "#15803d" },
    idle: { bg: "#ffedd5", color: "#ea580c" },
    stopped: { bg: "#fee2e2", color: "#dc2626" },
    inactive: { bg: "#dbeafe", color: "#2563eb" },
    expired: { bg: "#ede9fe", color: "#7c3aed" },
    no_data: { bg: "#e5e7eb", color: "#4b5563" },
  };
  return palette[status] || palette.no_data;
};

const getSidebarIndicatorText = (type, isOn) => {
  if (type === "gps") return isOn ? "Location Active" : "Location Inactive";
  if (type === "ign") return isOn ? "Engine Running" : "Engine OFF";
  return isOn ? "Power Connected" : "Power Disconnected";
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DeviceSidebar({
  devices = [],
  selectedDevices = [],
  setSelectedDevices,
  deviceCounts = {},
  totalDeviceCount = 0,
  initialStatus = "all",
  setSearchTerm,
  searchTerm = "",
  onDeviceClick,
  socketUpdatesRef,
  onToggleVisibility,
  handleOpenCommandModal,
  onOpenGeofence,
  onOpenReport,
  getVehicleIcon,
  liveStatuses = {},
  open = true,
  onToggle,
  onEditDevice,
  onAddDevice,
  onSelectGeofence,
  selectedGeofenceId = null,
  sidebarGeofences = [],
  onCreateSidebarGeofence,
  onEditSidebarGeofence,
  onDeleteSidebarGeofence,
  onAssignSidebarGeofence,
  notifications = [],
  onSelectNotification,
  selectedNotificationId = null,
  onOpenPlayback,
  historySelection = null,
  onClosePlayback,
  mobileMode = false,
  progressiveLoading = {},
  modernTrackingLayout = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isCompactDesktopWidth = useMediaQuery("(max-width: 1536px)");
  const isCompactDesktopHeight = useMediaQuery("(max-height: 900px)");
  const compactMobile = mobileMode || isMobile;
  const compactDesktop = !compactMobile && (isCompactDesktopWidth || isCompactDesktopHeight);
  const compactModernSidebar = modernTrackingLayout && compactDesktop;
  const desktopRowTemplate = compactDesktop
  ? "22px 22px minmax(118px, 2fr) 34px 22px 22px 22px 32px"
  : "28px 28px minmax(160px, 2fr) 40px 24px 24px 24px 36px";
  const { showStatus } = useStatus();
  const currentUser = useSelector((state) => state.auth?.user);
  const currentUserId = currentUser?._id;
  const currentUserRole = safeLower(currentUser?.role);

  const [activeStatus, setActiveStatus] = useState("all");
  const [activeTab, setActiveTab] = useState("Objects");
  const [openGroups, setOpenGroups] = useState({ ungrouped: true });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [menuDevice, setMenuDevice] = useState(null);
  const [notificationMenuAnchor, setNotificationMenuAnchor] = useState(null);
  const [notificationMenuPosition, setNotificationMenuPosition] = useState(null);
  const [menuNotification, setMenuNotification] = useState(null);
  const [geofenceMenuAnchor, setGeofenceMenuAnchor] = useState(null);
  const [remarkAlarm, setRemarkAlarm] = useState(null);
  const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDevice, setShareDevice] = useState(null);
  const [sharePreset, setSharePreset] = useState("1");
  const [shareCustomHours, setShareCustomHours] = useState("1");
  const [shareLink, setShareLink] = useState("");
  const [shareExpiresAt, setShareExpiresAt] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [historyDeviceDropdownOpen, setHistoryDeviceDropdownOpen] = useState(false);
  const [historyDeviceQuery, setHistoryDeviceQuery] = useState("");
  const [historyDeviceId, setHistoryDeviceId] = useState("");
  const [historyFilter, setHistoryFilter] = useState("Today");
  const [notificationEventFilter, setNotificationEventFilter] = useState("all");
  const [historyFromDate, setHistoryFromDate] = useState(() => toYMD(new Date()));
  const [historyToDate, setHistoryToDate] = useState(() => toYMD(new Date()));
  const [historyFromHour, setHistoryFromHour] = useState("12");
  const [historyFromMinute, setHistoryFromMinute] = useState("00");
  const [historyFromMeridiem, setHistoryFromMeridiem] = useState("AM");
  const [historyToHour, setHistoryToHour] = useState("11");
  const [historyToMinute, setHistoryToMinute] = useState("59");
  const [historyToMeridiem, setHistoryToMeridiem] = useState("PM");
  const [historyResults, setHistoryResults] = useState([]);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyExportAnchor, setHistoryExportAnchor] = useState(null);
  const { openModal: openQuickModal } = useQuickModal();
  const lastAutoPlaybackKeyRef = useRef("");
  const pendingHistoryPlaybackTargetRef = useRef(null);

  const sidebarOpen = open;
  const setSidebarOpen = onToggle || (() => {});

  const scrollRef = useRef(null);
  const objectQuerySeqRef = useRef(0);
  const trackingStateByDeviceRef = useRef(new Map());
  const historyImportInputRef = useRef(null);
  const historyDeviceDropdownRef = useRef(null);
  const statusPillsRef = useRef(null);
  const [objectsListHeight, setObjectsListHeight] = useState(480);
  const objectsListHeightRef = useRef(480);
  const [pagedObjectDevices, setPagedObjectDevices] = useState([]);
  const [pagedObjectsPage, setPagedObjectsPage] = useState(0);
  const [pagedObjectsHasMore, setPagedObjectsHasMore] = useState(false);
  const [pagedObjectsLoading, setPagedObjectsLoading] = useState(false);
  const [localDeviceCounts, setLocalDeviceCounts] = useState({});
  const pagedObjectDevicesRef = useRef([]);
  const pagedObjectsLoadingRef = useRef(false);
  const localDeviceCountsRef = useRef({});
  const [loadDevicesPage] = useLazyGetCompactFleetDevicesQuery();
  const [updateAlarmRemark, { isLoading: savingRemark }] = useUpdateAlarmRemarkMutation();
  const sidebarPageCacheParams = useMemo(
    () => ({
      status: activeStatus,
      search: String(searchTerm || "").trim(),
      limit: SIDEBAR_OBJECTS_PAGE_SIZE,
    }),
    [activeStatus, searchTerm]
  );

  const scrollStatusPills = useCallback((direction) => {
    const node = statusPillsRef.current;
    if (!node) return;
    const delta = Math.max(120, Math.floor((node.clientWidth || 0) * 0.55));
    node.scrollBy({
      left: direction === "left" ? -delta : delta,
      behavior: "smooth",
    });
  }, []);

  const resetHistoryRangeToToday = useCallback(() => {
    const today = toYMD(new Date());
    setHistoryFilter("Today");
    setHistoryFromDate(today);
    setHistoryToDate(today);
    setHistoryFromHour("12");
    setHistoryFromMinute("00");
    setHistoryFromMeridiem("AM");
    setHistoryToHour("11");
    setHistoryToMinute("59");
    setHistoryToMeridiem("PM");
  }, []);

  useEffect(() => {
    pagedObjectDevicesRef.current = Array.isArray(pagedObjectDevices) ? pagedObjectDevices : [];
  }, [pagedObjectDevices]);

  useEffect(() => {
    pagedObjectsLoadingRef.current = pagedObjectsLoading;
  }, [pagedObjectsLoading]);

  useEffect(() => {
    localDeviceCountsRef.current = localDeviceCounts || {};
  }, [localDeviceCounts]);

  useEffect(() => {
    if (activeTab !== "Objects") return;

    const cachedPayload = readDeviceViewCache(DEVICE_SIDEBAR_PAGE_CACHE_SCOPE, {
      userId: currentUserId,
      params: sidebarPageCacheParams,
    });
    if (!Array.isArray(cachedPayload?.rows) || !cachedPayload.rows.length) return;

    setPagedObjectDevices(cachedPayload.rows);
    setPagedObjectsPage(Number(cachedPayload.page || 1) || 1);
    setPagedObjectsHasMore(Boolean(cachedPayload.hasMore));
    if (cachedPayload.counts) {
  setLocalDeviceCounts(cachedPayload.counts);
}
  }, [activeTab, currentUserId, sidebarPageCacheParams]);

  useEffect(() => {
    const allowed = new Set(["all","running","idle","stopped","inactive","offline","no_data","nodata","expired"]);
    const raw = String(initialStatus || "all").trim();
    const normalizedInput = raw
      .replace(/([a-z])([A-Z])/g, "$1_$2")
      .replace(/[\s-]+/g, "_")
      .toLowerCase();
    const aliasMap = {
      nodata: "no_data",
      no_data: "no_data",
      no_reporting: "inactive",
      noreporting: "inactive",
      offline: "inactive",
      expired: "expired",
    };
    const next = aliasMap[normalizedInput] || normalizedInput;
    if (!allowed.has(next)) return;
    setActiveStatus(next);
  }, [initialStatus]);

  const { data: groupsResponse } = useGetAllGroupsQuery(undefined);
  const groups = useMemo(() => groupsResponse?.data || [], [groupsResponse]);

  useEffect(() => {
    objectsListHeightRef.current = objectsListHeight;
  }, [objectsListHeight]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return undefined;

    const updateHeight = () => {
      const nextHeight = Math.max(260, Number(node.clientHeight || 0));
      if (Math.abs(nextHeight - Number(objectsListHeightRef.current || 0)) <= 1) {
        return;
      }
      objectsListHeightRef.current = nextHeight;
      setObjectsListHeight(nextHeight);
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(node);
    window.addEventListener("resize", updateHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  const socketCtx = useContext(SocketContext);
  const connected = socketCtx?.connected;
  const getDeviceSocketData = socketCtx?.getDeviceSocketData;
  const onSocketEvent = socketCtx?.on;
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!onSocketEvent) return undefined;
    // Coalesce socket bursts to one re-render every 2s. Row live-data is read
    // from socketUpdatesRef at render time, so a missed bump just delays a
    // status pill / address refresh — never a map marker.
    let timer = null;
    let pending = false;
    const bump = () => {
      pending = true;
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        if (pending) {
          pending = false;
          setTick((t) => t + 1);
        }
      }, 2000);
    };
    const off = [
      onSocketEvent("gps_packet", bump),
      onSocketEvent("location_update", bump),
      onSocketEvent("gps_update", bump),
      onSocketEvent("status_update", bump),
      onSocketEvent("vehicle_update", bump),
      onSocketEvent("device_update", bump),
    ];
    return () => {
      off.forEach((fn) => fn?.());
      if (timer) clearTimeout(timer);
    };
  }, [onSocketEvent]);

  const allDevicesList = useMemo(() => {
    if (Array.isArray(devices)) return devices;
    if (Array.isArray(devices?.data)) return devices.data;
    return [];
  }, [devices]);

  const getLiveStatusInput = useCallback((device) => {
    const byRef = socketUpdatesRef?.current?.[device?._id] || socketUpdatesRef?.current?.[device?.imei] || {};
    const byHook = getDeviceSocketData ? getDeviceSocketData(device?._id || device?.imei) : {};
    const byProp = liveStatuses?.[device?._id] || liveStatuses?.[device?.imei] || {};
    const candidates = [byRef, byHook, byProp].filter((item) => item && Object.keys(item).length > 0);
    if (!candidates.length) return {};
    return candidates.sort((a, b) =>
      toTimestampMs(b?.packetReceivedAt ?? b?.lastSocketUpdate ?? b?.packetTime ?? b?.timestamp ?? b?.serverTime ?? b?.lastDataReceivedAt ?? b?.lastUpdate) -
      toTimestampMs(a?.packetReceivedAt ?? a?.lastSocketUpdate ?? a?.packetTime ?? a?.timestamp ?? a?.serverTime ?? a?.lastDataReceivedAt ?? a?.lastUpdate)
    )[0];
  }, [socketUpdatesRef, getDeviceSocketData, liveStatuses]);

  const matchesActiveSidebarStatus = useCallback((device) => {
    if (activeStatus === "all") return true;
    const live = getLiveStatusInput(device);
    return getBackendSidebarStatus(device, live) === activeStatus;
  }, [activeStatus, getLiveStatusInput]);

  const fetchObjectDevicesPage = useCallback(async ({ page = 1, reset = false } = {}) => {
    if (activeTab !== "Objects") return;
    if (pagedObjectsLoadingRef.current && !reset) return;

    const requestSeq = reset ? objectQuerySeqRef.current + 1 : objectQuerySeqRef.current;
    if (reset) objectQuerySeqRef.current = requestSeq;

    setPagedObjectsLoading(true);
    try {
  let latestCounts = localDeviceCountsRef.current;
  let nextPage = page;
      let hasMore = false;
      let mergedRows = reset ? [] : [...pagedObjectDevicesRef.current];
      let matchedRows = reset ? [] : mergedRows.filter(matchesActiveSidebarStatus);
      const minimumVisibleMatches = activeStatus === "all" ? SIDEBAR_OBJECTS_PAGE_SIZE : 30;

      do {
        const payload = await loadDevicesPage(
          {
  page: nextPage,
  limit: SIDEBAR_OBJECTS_PAGE_SIZE,
  includeRelations: "false",
  includeExpired: "true",
  ...(activeStatus !== "all" ? { status: activeStatus } : {}),
  ...(searchTerm?.trim() ? { search: searchTerm.trim() } : {}),
},
          true
        ).unwrap();

        if (requestSeq !== objectQuerySeqRef.current) return;

        const rows = Array.isArray(payload?.data) ? payload.data : [];
if (payload?.counts) {
  latestCounts = payload.counts;
  setLocalDeviceCounts(payload.counts);
}
        mergedRows = nextPage === 1 && reset ? rows : [...mergedRows, ...rows];
        matchedRows = mergedRows.filter(matchesActiveSidebarStatus);
        hasMore = Boolean(payload?.hasMore);
        nextPage = Number(payload?.page || nextPage);

        if (!reset) break;
        if (activeStatus === "all" && mergedRows.length >= SIDEBAR_OBJECTS_PAGE_SIZE) break;
        if (matchedRows.length >= minimumVisibleMatches) break;
        if (!hasMore) break;
        nextPage += 1;
      } while (hasMore);

      if (
        reset &&
        mergedRows.length === 0 &&
        ["user", "driver", "client", "viewer"].includes(currentUserRole)
      ) {
        const localFallbackRows = (Array.isArray(allDevicesList) ? allDevicesList : []).filter((device) => {
          const query = String(searchTerm || "").trim().toLowerCase();
          if (activeStatus !== "all" && !matchesActiveSidebarStatus(device)) return false;
          if (!query) return true;
          return [
            device?.name,
            device?.vehicleNumber,
            device?.imei,
            device?.msdn,
          ].some((value) => String(value || "").toLowerCase().includes(query));
        });

        mergedRows = localFallbackRows.slice(0, SIDEBAR_OBJECTS_PAGE_SIZE);
        matchedRows = mergedRows;
        hasMore = localFallbackRows.length > mergedRows.length;
        nextPage = mergedRows.length ? 1 : 0;
      }

      setPagedObjectDevices(mergedRows);
      setPagedObjectsPage(nextPage);
      setPagedObjectsHasMore(hasMore);
      writeDeviceViewCache(DEVICE_SIDEBAR_PAGE_CACHE_SCOPE, {
        userId: currentUserId,
        params: sidebarPageCacheParams,
       payload: {
  rows: mergedRows,
  page: nextPage,
  hasMore,
counts: latestCounts,
},
      });
    } catch (error) {
      if (reset) {
        setPagedObjectDevices([]);
        setPagedObjectsPage(0);
        setPagedObjectsHasMore(false);
      }
    } finally {
      if (requestSeq === objectQuerySeqRef.current) {
        setPagedObjectsLoading(false);
      }
    }
  }, [
    activeStatus,
    activeTab,
    allDevicesList,
    currentUserId,
    currentUserRole,
    loadDevicesPage,
    matchesActiveSidebarStatus,
    searchTerm,
    sidebarPageCacheParams,
  ]);

  const fetchObjectDevicesPageRef = useRef(fetchObjectDevicesPage);

  useEffect(() => {
    fetchObjectDevicesPageRef.current = fetchObjectDevicesPage;
  }, [fetchObjectDevicesPage]);

  useEffect(() => {
    if (activeTab !== "Objects") return undefined;
    const timer = setTimeout(() => {
      fetchObjectDevicesPageRef.current({ page: 1, reset: true });
    }, searchTerm?.trim() ? 220 : 20);
    return () => clearTimeout(timer);
  }, [activeStatus, activeTab, searchTerm]);

  const statusCounts = useMemo(() => {
    const query = safeLower(searchTerm).trim();
    const pagedRows = Array.isArray(pagedObjectDevices) ? pagedObjectDevices : [];
    const pagedSearchMatches = query
      ? pagedRows.filter((device) => matchesDeviceSearch(device, query))
      : pagedRows;
    const searchMatchedDevices = query
      ? (pagedSearchMatches.length
          ? pagedSearchMatches
          : allDevicesList.filter((device) => matchesDeviceSearch(device, query)))
      : allDevicesList;
    const sourceDevices = activeStatus !== "all"
      ? searchMatchedDevices.filter((device) => {
          const live = getLiveStatusInput(device);
          return getBackendSidebarStatus(device, live) === activeStatus;
        })
      : searchMatchedDevices;

    const derivedCounts = {
      all: sourceDevices.length,
      running: 0,
      idle: 0,
      stopped: 0,
      inactive: 0,
      expired: 0,
      no_data: 0,
    };

    for (const d of sourceDevices) {
      const live = getLiveStatusInput(d);
      const s = getBackendSidebarStatus(d, live);
      derivedCounts[s] = (derivedCounts[s] || 0) + 1;
    }

    if (query) {
      return derivedCounts;
    }

    const sourceCounts =
      Object.keys(localDeviceCounts || {}).length
        ? localDeviceCounts
        : deviceCounts || {};

    const authoritativeTotal = Math.max(
      Number(totalDeviceCount) || 0,
      Number(sourceCounts?.total) || 0,
      Number(sourceCounts?.all) || 0,
      derivedCounts.all
    );

    // Use live-derived counts for fast-changing active states,
    // but preserve backend summary counts as a floor for sparse buckets
    // like no_data/inactive/expired that may not be fully materialized locally.
    const running = derivedCounts.running;
    const idle = derivedCounts.idle;
    const inactive = Math.max(
      derivedCounts.inactive,
      Number(sourceCounts?.inactive || 0)
    );
    const expired = Math.max(
      derivedCounts.expired,
      Number(sourceCounts?.expired || 0)
    );
    const noData = Math.max(
      derivedCounts.no_data,
      Number(sourceCounts?.no_data ?? sourceCounts?.nodata ?? 0)
    );
    const stopped = Math.max(0, authoritativeTotal - running - idle - inactive - expired - noData);

    return {
      all: authoritativeTotal,
      running,
      idle,
      stopped,
      inactive,
      expired,
      no_data: noData,
    };
  }, [activeStatus, allDevicesList, deviceCounts, getLiveStatusInput, localDeviceCounts, pagedObjectDevices, searchTerm, totalDeviceCount]);

  const filterList = useCallback((list) => {
    let result = Array.isArray(list) ? list : [];
    const q = safeLower(searchTerm).trim();
    if (q) {
      result = result.filter((d) => matchesDeviceSearch(d, q));
    }
    if (activeStatus !== "all") {
      result = result.filter((d) => {
        const live = getLiveStatusInput(d);
        return getBackendSidebarStatus(d, live) === activeStatus;
      });
    }

    // Keep moving vehicles immediately visible in the default All view. Do not
    // change the ordering inside individual status filters.
    if (activeStatus === "all") {
      result = [...result].sort((left, right) => {
        const leftRunning = getBackendSidebarStatus(left, getLiveStatusInput(left)) === "running";
        const rightRunning = getBackendSidebarStatus(right, getLiveStatusInput(right)) === "running";
        return Number(rightRunning) - Number(leftRunning);
      });
    }
    return result;
  }, [activeStatus, getLiveStatusInput, searchTerm]);

  const objectSourceDevices = useMemo(() => {
    if (activeTab === "Objects") {
      const pagedRows = Array.isArray(pagedObjectDevices) ? pagedObjectDevices : [];
      const fullRows = Array.isArray(allDevicesList) ? allDevicesList : [];

      // For filtered buckets like inactive / expired / no_data, prefer the richer
      // in-memory source if it contains more matching devices than the current page
      // snapshot. This keeps status counts and visible rows aligned.
      if (activeStatus !== "all") {
        const pagedMatches = filterList(pagedRows);
        const fullMatches = filterList(fullRows);
        if (fullMatches.length > pagedMatches.length) {
          return fullRows;
        }
      }

      if (pagedRows.length > 0) {
        return pagedRows;
      }
      return fullRows;
    }
    return allDevicesList;
  }, [activeStatus, activeTab, allDevicesList, filterList, pagedObjectDevices]);

  const visibleFilteredDevices = useMemo(() => {
    if (activeTab !== "Objects") return [];
    return filterList(objectSourceDevices);
  }, [activeTab, filterList, objectSourceDevices]);

  const visibleObjectStatusCounts = useMemo(() => {
    const counts = {
      all: 0,
      running: 0,
      idle: 0,
      stopped: 0,
      inactive: 0,
      expired: 0,
      no_data: 0,
    };

    visibleFilteredDevices.forEach((device) => {
      if (!device) return;
      counts.all += 1;
      const live = getLiveStatusInput(device);
      const status = getBackendSidebarStatus(device, live);
      counts[status] = (counts[status] || 0) + 1;
    });

    return counts;
  }, [getLiveStatusInput, visibleFilteredDevices]);

  const statusPillCounts = useMemo(() => {
    const hasSearchQuery = Boolean(safeLower(searchTerm).trim());
    return activeTab === "Objects" && hasSearchQuery ? visibleObjectStatusCounts : statusCounts;
  }, [activeTab, searchTerm, statusCounts, visibleObjectStatusCounts]);

  const groupedDevices = useMemo(() => {
    const grouped = {};
    const used = new Set();
    for (const g of groups) {
      const ids = g?.devices?.map((d) => (typeof d === "string" ? d : d?._id)).filter(Boolean) || [];
      grouped[g._id] = { name: g?.name || "Group", devices: objectSourceDevices.filter((d) => ids.includes(d?._id)) };
      ids.forEach((id) => used.add(id));
    }
    grouped.ungrouped = { name: "Ungrouped", devices: objectSourceDevices.filter((d) => d?._id && !used.has(d._id)) };
    return grouped;
  }, [groups, objectSourceDevices]);

  const progressiveStatusText = useMemo(() => {
    const activeSegments = [];
    if (progressiveLoading?.runningIdle) activeSegments.push("Running/Idle");
    if (progressiveLoading?.stopped) activeSegments.push("Stopped");
    if (progressiveLoading?.inactive) activeSegments.push("No Reporting");
    return activeSegments.length ? `Loading ${activeSegments.join(" • ")}` : "";
  }, [progressiveLoading]);

  const geofenceItems = useMemo(() => {
    const query = safeLower(searchTerm).trim();

    if (Array.isArray(sidebarGeofences) && sidebarGeofences.length) {
      const rows = sidebarGeofences.map((item, index) => ({
        id: String(item?.id || item?._id || `shared-geofence-${index}`),
        _id: item?._id || item?.id || null,
        name: item?.name || `Geofence ${index + 1}`,
        type: item?.type || (item?.radius ? "circle" : "polygon"),
        category: item?.category || "",
        locationLabel: item?.locationLabel || "",
        assignedCount: Number(item?.assignedCount || item?.assignedDeviceIds?.length || 0),
        assignedDeviceIds: Array.isArray(item?.assignedDeviceIds) ? item.assignedDeviceIds : [],
        radiusText: item?.radiusText || "",
        raw: item,
      }));

      if (!query) return rows;
      return rows.filter((item) =>
        safeLower(item.name).includes(query) ||
        safeLower(item.category).includes(query) ||
        safeLower(item.locationLabel).includes(query)
      );
    }

    const items = [];
    allDevicesList.forEach((device) => {
      const deviceGeofences = Array.isArray(device?.geofences) ? device.geofences : [];
      deviceGeofences.forEach((geofence, index) => {
        if (geofence?.isActive === false) return;
        const geofenceId =
          geofence?.sourceGeofenceId ||
          geofence?._id ||
          geofence?.id ||
          `${device?._id || device?.imei || "device"}-${geofence?.name || "geofence"}-${index}`;
        items.push({
          id: String(geofenceId),
          name: geofence?.name || `Geofence ${index + 1}`,
          type: geofence?.type || (geofence?.radius ? "circle" : "polygon"),
          deviceId: device?._id,
          deviceName: device?.name || device?.vehicleNumber || "Unnamed device",
          category: geofence?.category || "",
          locationLabel: geofence?.locationLabel || "",
        });
      });
    });

    if (!query) return items;
    return items.filter((item) =>
      safeLower(item.name).includes(query) ||
      safeLower(item.deviceName).includes(query) ||
      safeLower(item.category).includes(query) ||
      safeLower(item.locationLabel).includes(query)
    );
  }, [allDevicesList, searchTerm, sidebarGeofences]);

  const historyDeviceOptions = useMemo(() => {
    const query = safeLower(historyDeviceQuery).trim();
    const scored = allDevicesList
      .map((device) => {
        const label = device?.name || device?.vehicleNumber || device?.imei || "";
        const name = safeLower(device?.name);
        const imei = safeLower(device?.imei);
        const vehicle = safeLower(device?.vehicleNumber);
        const labelLower = safeLower(label);
        const matches = !query || name.includes(query) || imei.includes(query) || vehicle.includes(query);
        if (!matches) return null;

        let score = 3;
        if (labelLower === query || imei === query || vehicle === query) score = 0;
        else if (labelLower.startsWith(query) || vehicle.startsWith(query) || imei.startsWith(query)) score = 1;
        else if (labelLower.includes(query) || vehicle.includes(query) || imei.includes(query)) score = 2;

        return { device, score, label };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.score !== b.score) return a.score - b.score;
        return String(a.label).localeCompare(String(b.label));
      })
      .map((entry) => entry.device);

    return scored.slice(0, 14);
  }, [allDevicesList, historyDeviceQuery]);

  const selectedHistoryDevice = useMemo(
    () => allDevicesList.find((device) => String(device?._id) === String(historyDeviceId)) || null,
    [allDevicesList, historyDeviceId]
  );

  const notificationItems = useMemo(() => {
    const query = safeLower(searchTerm).trim();
    const eventValues = [];
    const filtered = (Array.isArray(notifications) ? notifications : []).filter((alarm) => {
      const eventName = String(alarm?.alarmType || alarm?.eventType || "");
      if (eventName) eventValues.push(eventName);
      const deviceLabel = safeLower(alarm?.deviceName || alarm?.imei || "");
      const eventLabel = safeLower(prettyNotificationEvent(eventName));
      const matchesQuery = !query || deviceLabel.includes(query) || eventLabel.includes(query);
      return matchesQuery;
    });

    return {
      rows: filtered,
      eventOptions: Array.from(new Set(eventValues)).sort((a, b) => a.localeCompare(b)),
    };
  }, [notifications, searchTerm]);

  useEffect(() => {
    if (!selectedHistoryDevice) return;
    setHistoryDeviceQuery(getHistoryDeviceLabel(selectedHistoryDevice));
  }, [selectedHistoryDevice]);

  useEffect(() => {
    if (!historySelection?.deviceId) return;
    setActiveTab("History");
    setHistoryDeviceId(historySelection.deviceId);
    setHistoryDeviceQuery(historySelection.deviceName || "");
    setHistoryDeviceDropdownOpen(false);
    pendingHistoryPlaybackTargetRef.current = historySelection;
    setHistoryVisible(false);
    setHistoryError("");
  }, [historySelection]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!historyDeviceDropdownRef.current?.contains(event.target)) {
        setHistoryDeviceDropdownOpen(false);
      }
    };

    if (!historyDeviceDropdownOpen) return undefined;
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [historyDeviceDropdownOpen]);

  useEffect(() => {
    if (historyFilter !== "Today") return;
    const today = toYMD(new Date());
    setHistoryFromDate(today);
    setHistoryToDate(today);
    setHistoryFromHour("12");
    setHistoryFromMinute("00");
    setHistoryFromMeridiem("AM");
    setHistoryToHour("11");
    setHistoryToMinute("59");
    setHistoryToMeridiem("PM");
  }, [historyFilter]);

  useEffect(() => {
    if (!historyVisible || !historyResults.length || !historyDeviceId) return;

    const pendingRows = historyResults.filter(
      (row) =>
        (!row.startAddressResolved && (row.startAddress === "NA" || !row.startAddress) && row.startCoords) ||
        (!row.endAddressResolved && (row.endAddress === "NA" || !row.endAddress) && row.endCoords)
    );

    if (!pendingRows.length) return;

    let cancelled = false;
    const baseUrl =
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_API_URL ||
      "https://app.krush.in/api";

    const resolveAddress = async (coords) => {
      if (!coords?.lat || !coords?.lng) return "NA";
      try {
        const response = await fetch(
          `${baseUrl}/geocode/reverse?lat=${coords.lat}&lon=${coords.lng}`,
          {
            headers: { "Content-Type": "application/json" },
          }
        );
        if (!response.ok) return "NA";
        const data = await response.json();
        return (
          data?.address ||
          data?.address?.full ||
          data?.address?.short ||
          "NA"
        );
      } catch {
        return "NA";
      }
    };

    (async () => {
      const resolvedRows = await Promise.all(
        pendingRows.map(async (row) => {
          const [nextStart, nextEnd] = await Promise.all([
            row.startAddress === "NA" || !row.startAddress
              ? resolveAddress(row.startCoords)
              : Promise.resolve(row.startAddress),
            row.endAddress === "NA" || !row.endAddress
              ? resolveAddress(row.endCoords)
              : Promise.resolve(row.endAddress),
          ]);

          return {
            id: row.id,
            startAddress: nextStart,
            endAddress: nextEnd,
          };
        })
      );

      if (cancelled) return;

      const resolvedById = new Map(resolvedRows.map((row) => [row.id, row]));
      setHistoryResults((prev) =>
        prev.map((item) => {
          const resolved = resolvedById.get(item.id);
          if (!resolved) return item;
          return {
            ...item,
            startAddress: resolved.startAddress || item.startAddress,
            endAddress: resolved.endAddress || item.endAddress,
            startAddressResolved: true,
            endAddressResolved: true,
          };
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [historyVisible, historyResults, historyDeviceId]);

  useEffect(() => {
    if (groups.length && Object.keys(openGroups || {}).length <= 1) {
      const next = { ungrouped: true };
      for (let i = 0; i < Math.min(2, groups.length); i++) next[groups[i]._id] = true;
      setOpenGroups(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const toggleGroup = (key) => setOpenGroups((p) => ({ ...p, [key]: !p[key] }));

  const objectRows = useMemo(() => {
    if (modernTrackingLayout) {
      return filterList(objectSourceDevices).map((device) => ({
        type: "device",
        key: `device-${device._id}`,
        device,
      }));
    }

    const rows = [];
    const forceExpandFilteredGroups =
      activeTab === "Objects" || activeStatus !== "all" || Boolean(safeLower(searchTerm).trim());
    Object.entries(groupedDevices).forEach(([key, group]) => {
      const list = filterList(group.devices);
      if (!list.length) return;
      rows.push({
        type: "group",
        key: `group-${key}`,
        groupKey: key,
        groupName: group.name,
        devices: list,
      });
      if (forceExpandFilteredGroups || openGroups[key]) {
        list.forEach((device) => {
          rows.push({
            type: "device",
            key: `device-${device._id}`,
            device,
          });
        });
      }
    });
    return rows;
  }, [activeStatus, activeTab, groupedDevices, filterList, modernTrackingLayout, objectSourceDevices, openGroups, searchTerm]);

  const closeHistoryExportMenu = () => setHistoryExportAnchor(null);

  const openDeviceHistoryPanel = useCallback((device) => {
    if (!device) return;
    const label = getHistoryDeviceLabel(device);
    setActiveTab("History");
    setHistoryDeviceId(String(device?._id || ""));
    setHistoryDeviceQuery(label);
    setHistoryDeviceDropdownOpen(false);
    resetHistoryRangeToToday();
    setHistoryVisible(false);
    setHistoryResults([]);
    setHistoryError("");
    lastAutoPlaybackKeyRef.current = "";
  }, [resetHistoryRangeToToday]);

  const fetchHistoryReport = useCallback(async () => {
    let resolvedHistoryDeviceId = String(historyDeviceId || "");
    if (!resolvedHistoryDeviceId && historyDeviceQuery?.trim()) {
      const matchedDevice = allDevicesList
        .map((device) => ({
          device,
          label: getHistoryDeviceLabel(device),
        }))
        .find(({ label }) => safeLower(label).includes(safeLower(historyDeviceQuery).trim()))?.device;
      if (matchedDevice?._id) {
        resolvedHistoryDeviceId = String(matchedDevice._id);
        setHistoryDeviceId(resolvedHistoryDeviceId);
        setHistoryDeviceQuery(getHistoryDeviceLabel(matchedDevice) || historyDeviceQuery);
      }
    }

    if (!resolvedHistoryDeviceId) {
      setHistoryError("Please select a device.");
      setHistoryResults([]);
      setHistoryVisible(false);
      return;
    }

    const isTodayFilter = String(historyFilter || "").toLowerCase() === "today";
    const effectiveToday = toYMD(new Date());
    const effectiveFromDate = isTodayFilter ? effectiveToday : historyFromDate;
    const effectiveToDate = isTodayFilter ? effectiveToday : historyToDate;
    const effectiveFromHour = isTodayFilter ? "12" : historyFromHour;
    const effectiveFromMinute = isTodayFilter ? "00" : historyFromMinute;
    const effectiveFromMeridiem = isTodayFilter ? "AM" : historyFromMeridiem;
    const effectiveToHour = isTodayFilter ? "11" : historyToHour;
    const effectiveToMinute = isTodayFilter ? "59" : historyToMinute;
    const effectiveToMeridiem = isTodayFilter ? "PM" : historyToMeridiem;

    const startDt = buildDateTimeFromParts(
      effectiveFromDate,
      effectiveFromHour,
      effectiveFromMinute,
      effectiveFromMeridiem
    );
    const endDt = buildDateTimeFromParts(
      effectiveToDate,
      effectiveToHour,
      effectiveToMinute,
      effectiveToMeridiem
    );
    if (!startDt || !endDt || startDt > endDt) {
      setHistoryError("Please choose a valid date range.");
      setHistoryResults([]);
      setHistoryVisible(false);
      return;
    }

    setHistoryLoading(true);
    setHistoryError("");
    try {
      const token = localStorage.getItem("token");
      const apiBase =
        process.env.REACT_APP_BACKEND_URL ||
        process.env.REACT_APP_API_URL ||
        "https://app.krush.in/api";
      const params = new URLSearchParams({
        startDate: effectiveFromDate,
        endDate: effectiveToDate,
      });
      const resp = await fetch(`${apiBase}/history/${resolvedHistoryDeviceId}/report?${params.toString()}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "x-auth-token": token || "",
          "Content-Type": "application/json",
        },
      });
      if (!resp.ok) throw new Error(`History API failed (${resp.status})`);
      const data = await resp.json();
      const rides = Array.isArray(data?.rides) ? data.rides : [];
      const dailyData = Array.isArray(data?.dailyData) ? data.dailyData : [];
      let normalized = (rides.length ? rides : dailyData).map((item, index) => {
        const date = item?.date || effectiveFromDate;
        const startTime = item?.startTime || `${date}T00:00:00`;
        const endTime = item?.endTime || `${date}T00:00:00`;
        const distanceValue = Number(item?.distanceRawKm ?? item?.distance ?? 0);
        const durationValue = Number(item?.movingTime ?? item?.duration ?? 0);
        return {
          id: String(item?.id || item?._id || `${date}-${index}`),
          startTimeRaw: startTime,
          endTimeRaw: endTime,
          startLabel: formatHistoryDateLabel(startTime),
          endLabel: formatHistoryDateLabel(endTime),
          startAddress: extractHistoryAddress(item, "start"),
          endAddress: extractHistoryAddress(item, "end"),
          startCoords: extractHistoryCoords(item, "start"),
          endCoords: extractHistoryCoords(item, "end"),
          startAddressResolved: extractHistoryAddress(item, "start") !== "NA",
          endAddressResolved: extractHistoryAddress(item, "end") !== "NA",
          durationText: formatDurationMins(durationValue),
          distanceText: formatHistoryDistance(distanceValue),
          isRide: distanceValue > 0.05,
        };
      }).filter((item) => {
        const tripStart = new Date(item.startTimeRaw).getTime();
        const tripEnd = new Date(item.endTimeRaw).getTime();
        const rangeStart = startDt.getTime();
        const rangeEnd = endDt.getTime();
        if (!Number.isFinite(tripStart) || !Number.isFinite(tripEnd)) return true;
        return tripEnd >= rangeStart && tripStart <= rangeEnd;
      });

      const shouldUsePlaybackFallback =
        !rides.length &&
        (
          normalized.length <= 1 ||
          normalized.every(
            (item) =>
              !item.isRide &&
              (!item.startAddressResolved || item.startAddress === "NA") &&
              (!item.endAddressResolved || item.endAddress === "NA") &&
              safeLower(item.distanceText).startsWith("0 ")
          )
        );

      if (shouldUsePlaybackFallback) {
        const fallbackPlaybackResp = await fetch(
          `${apiBase}/history/${resolvedHistoryDeviceId}/playback?startDate=${encodeURIComponent(effectiveFromDate)}&endDate=${encodeURIComponent(effectiveToDate)}&startTs=${encodeURIComponent(startDt.toISOString())}&endTs=${encodeURIComponent(endDt.toISOString())}`,
          {
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
              "x-auth-token": token || "",
              "Content-Type": "application/json",
            },
          }
        );
        if (fallbackPlaybackResp.ok) {
          const fallbackPlayback = await fallbackPlaybackResp.json();
          const fallbackPoints = Array.isArray(fallbackPlayback?.points)
            ? fallbackPlayback.points
            : [];
          const playbackRows = buildHistoryRowsFromPlaybackPoints(fallbackPoints);
          if (playbackRows.length) {
            normalized = playbackRows;
          }
        }
      }

      setHistoryResults(normalized);
      setHistoryVisible(true);
      if (!normalized.length) setHistoryError("No history found for selected range.");

      // Auto-open playback when history is fetched and we have at least one ride.
      // Uses /history/:deviceId/playback so backend stays fast (no geocoding).
      if (normalized.length && typeof onOpenPlayback === "function") {
        const pendingPlaybackTarget = pendingHistoryPlaybackTargetRef.current;
        const playbackStartRaw = pendingPlaybackTarget?.startTimestamp || normalized[0]?.startTimeRaw;
        const playbackEndRaw = pendingPlaybackTarget?.endTimestamp || normalized[0]?.endTimeRaw;
        const playbackStartDate = toYMD(playbackStartRaw) || effectiveFromDate;
        const playbackEndDate = toYMD(playbackEndRaw) || effectiveToDate;
        const playbackStartTs = new Date(
          playbackStartRaw || normalized[0]?.startTimeRaw
        ).toISOString();
        const playbackEndTs = new Date(
          playbackEndRaw || normalized[0]?.endTimeRaw
        ).toISOString();
        const key = `${resolvedHistoryDeviceId}:${playbackStartTs}:${playbackEndTs}`;
        if (lastAutoPlaybackKeyRef.current !== key) {
          lastAutoPlaybackKeyRef.current = key;
          const first = normalized[0];
          const pbResp = await fetch(
            `${apiBase}/history/${resolvedHistoryDeviceId}/playback?startDate=${encodeURIComponent(playbackStartDate)}&endDate=${encodeURIComponent(playbackEndDate)}&startTs=${encodeURIComponent(playbackStartTs)}&endTs=${encodeURIComponent(playbackEndTs)}`,
            {
              headers: {
                Authorization: token ? `Bearer ${token}` : "",
                "x-auth-token": token || "",
                "Content-Type": "application/json",
              },
            }
          );
          if (pbResp.ok) {
            const pb = await pbResp.json();
            const points = Array.isArray(pb?.points) ? pb.points : [];
            if (points.length) {
              onOpenPlayback({
                id: `history_${resolvedHistoryDeviceId}_${first.id}`,
                deviceId: resolvedHistoryDeviceId,
                date: first?.startLabel || effectiveFromDate,
                route: points,
                distance: Number(pb?.stats?.totalDistanceRawKm ?? 0),
                durationSeconds: Number(pb?.stats?.movingTime ?? 0) + Number(pb?.stats?.idleTime ?? 0),
                durationLabel: undefined,
                startTime: first.startTimeRaw,
                endTime: first.endTimeRaw,
              });
            }
          }
        }
      }
      pendingHistoryPlaybackTargetRef.current = null;
    } catch (error) {
      pendingHistoryPlaybackTargetRef.current = null;
      setHistoryError(error?.message || "Failed to load history.");
      setHistoryResults([]);
      setHistoryVisible(false);
    } finally {
      setHistoryLoading(false);
    }
  }, [
    allDevicesList,
    historyDeviceId,
    historyDeviceQuery,
    historyFilter,
    historyFromDate,
    historyFromHour,
    historyFromMinute,
    historyFromMeridiem,
    historyToDate,
    historyToHour,
    historyToMinute,
    historyToMeridiem,
    onOpenPlayback,
  ]);

  useEffect(() => {
    if (!historySelection?.deviceId) return;
    if (String(historyDeviceId || "") !== String(historySelection.deviceId || "")) return;
    fetchHistoryReport();
  }, [fetchHistoryReport, historyDeviceId, historySelection]);

  const exportHistoryData = useCallback((type) => {
    if (!historyResults.length) return;
    const payload = historyResults.map((item) => ({
      start_time: item.startLabel,
      start_address: item.startAddress,
      end_time: item.endLabel,
      end_address: item.endAddress,
      duration: item.durationText,
      distance: item.distanceText,
      trip_type: item.isRide ? "ride" : "stop",
    }));
    let blob;
    let filename;
    if (type === "json") {
      blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      filename = "history-export.json";
    } else {
      const header = "Start Time,Start Address,End Time,End Address,Duration,Distance,Trip Type\n";
      const rows = payload
        .map((row) => [row.start_time, row.start_address, row.end_time, row.end_address, row.duration, row.distance, row.trip_type]
          .map((value) => `"${String(value || "").replace(/"/g, '""')}"`).join(","))
        .join("\n");
      blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
      filename = "history-export.csv";
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    closeHistoryExportMenu();
  }, [historyResults]);

  const handleImportHistoryFile = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      let rows = [];
      if (file.name.toLowerCase().endsWith(".json")) {
        const parsed = JSON.parse(text);
        rows = Array.isArray(parsed) ? parsed : [];
      } else {
        const lines = text.split(/\r?\n/).filter(Boolean);
        rows = lines.slice(1).map((line, index) => {
          const parts = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g)?.map((part) => part.replace(/^"|"$/g, "").replace(/""/g, '"')) || [];
          return {
            id: `import-${index}`,
            startLabel: parts[0] || "",
            startAddress: parts[1] || "NA",
            endLabel: parts[2] || "",
            endAddress: parts[3] || "NA",
            durationText: parts[4] || "00:00 min",
            distanceText: parts[5] || "0 Kms",
            isRide: String(parts[6] || "").toLowerCase() === "ride",
          };
        });
      }
      const normalized = rows.map((item, index) => ({
        id: item.id || `import-${index}`,
        startLabel: item.startLabel || item.start_time || "",
        startAddress: item.startAddress || item.start_address || "NA",
        endLabel: item.endLabel || item.end_time || "",
        endAddress: item.endAddress || item.end_address || "NA",
        durationText: item.durationText || item.duration || "00:00 min",
        distanceText: item.distanceText || item.distance || "0 Kms",
        isRide: item.isRide ?? String(item.trip_type || "").toLowerCase() === "ride",
      }));
      setHistoryResults(normalized);
      setHistoryVisible(true);
      setHistoryError("");
    } catch (error) {
      setHistoryError("Failed to import history file.");
    } finally {
      event.target.value = "";
      closeHistoryExportMenu();
    }
  }, []);

 const openMoreMenu = (e, device) => {
  e.preventDefault();
  e.stopPropagation();

  const anchor = e.currentTarget;
  const rect = anchor.getBoundingClientRect();

  setMenuAnchor(anchor);
  setMenuPosition({
    top: Math.round(rect.top + rect.height / 2),
    left: Math.round(rect.left - 8),
  });
  setMenuDevice(device);
};
  const closeMoreMenu = () => { setMenuAnchor(null); setMenuPosition(null); setMenuDevice(null); };
  const openNotificationMenu = (e, alarm) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setNotificationMenuAnchor(e.currentTarget);
    setNotificationMenuPosition({
      top: Math.round(rect.top + rect.height / 2),
      left: Math.round(rect.left - 8),
    });
    setMenuNotification(alarm);
  };
  const closeNotificationMenu = () => {
    setNotificationMenuAnchor(null);
    setNotificationMenuPosition(null);
    setMenuNotification(null);
  };
  const openRemarkDialog = () => {
    setRemarkAlarm(menuNotification);
    setRemarkText(menuNotification?.notes || "");
    setRemarkDialogOpen(true);
    closeNotificationMenu();
  };
  const handleSaveRemark = async () => {
    if (!remarkAlarm?._id && !remarkAlarm?.alarmId) return;
    const alarmId = remarkAlarm?._id || remarkAlarm?.alarmId;
    try {
      await updateAlarmRemark({ alarmId, notes: remarkText }).unwrap();
      setRemarkDialogOpen(false);
      setRemarkAlarm((prev) => prev ? { ...prev, notes: remarkText } : prev);
    } catch {
      // keep dialog open on failure
    }
  };
  const handleRefreshClick = () => setTick((t) => t + 1);
  const resolveShareHours = useCallback(() => {
    if (sharePreset === "custom") {
      const parsed = Number(shareCustomHours);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return parsed;
    }
    const matched = SHARE_PRESETS.find((item) => item.value === sharePreset);
    return matched?.hours || 1;
  }, [shareCustomHours, sharePreset]);

  const generateShareLink = useCallback(async (device, requestedHours) => {
    if (!device?._id) return;
    setShareLoading(true);
    try {
      const response = await api.get(`/devices/${device._id}/share-link`, {
        params: { hours: requestedHours },
      });
      const token = response?.data?.token;
      const nextLink = `${window.location.origin}/live-tracking?t=${encodeURIComponent(token || "")}`;
      setShareLink(nextLink);
      setShareExpiresAt(response?.data?.expiresAt || "");
    } catch (error) {
      setShareLink("");
      setShareExpiresAt("");
      showStatus("error", error?.response?.data?.msg || "Failed to generate share link.");
    } finally {
      setShareLoading(false);
    }
  }, [showStatus]);

  const openShareDialog = useCallback((device) => {
    setShareDevice(device || null);
    setSharePreset("1");
    setShareCustomHours("1");
    setShareLink("");
    setShareExpiresAt("");
    setShareDialogOpen(true);
    if (device?._id) {
      generateShareLink(device, 1);
    }
  }, [generateShareLink]);

  const closeShareDialog = useCallback(() => {
    setShareDialogOpen(false);
    setShareDevice(null);
    setShareLink("");
    setShareExpiresAt("");
    setShareLoading(false);
  }, []);

  const handleRegenerateShareLink = useCallback(() => {
    const hours = resolveShareHours();
    if (!shareDevice?._id || !hours) {
      showStatus("warning", "Please enter valid share duration.");
      return;
    }
    generateShareLink(shareDevice, hours);
  }, [generateShareLink, resolveShareHours, shareDevice, showStatus]);

  const handleCopyShareLink = useCallback(async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      showStatus("success", "Share link copied.");
    } catch {
      window.prompt("Copy this link:", shareLink);
    }
  }, [shareLink, showStatus]);

  const handleOpenShareLink = useCallback(() => {
    if (!shareLink) return;
    window.open(shareLink, "_blank", "noopener,noreferrer");
  }, [shareLink]);

  const handleWhatsAppShare = useCallback(() => {
    if (!shareLink) return;
    const vehicleName = shareDevice?.name || shareDevice?.vehicleNumber || shareDevice?.imei || "Vehicle";
    const text = `Track ${vehicleName} live: ${shareLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  }, [shareDevice, shareLink]);

  const isAllSelected = allDevicesList.length > 0 && allDevicesList.every((d) => selectedDevices.includes(d._id));
  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedDevices?.([]);
    else setSelectedDevices?.(allDevicesList.map((d) => d._id));
  };
  const toggleSelectDevice = (id) => {
    if (selectedDevices.includes(id)) setSelectedDevices?.(selectedDevices.filter((x) => x !== id));
    else setSelectedDevices?.([...selectedDevices, id]);
  };

  // ── DeviceRow ──────────────────────────────────────────────────────────────
  const DeviceRow = React.memo(function DeviceRow({ device, liveInput }) {
    const deviceKey = String(device?._id || device?.imei || "");
    const socketData = liveInput || {};
    const apiLive = device?.deviceLive || device?.liveSnapshot || {};

    const socketTs = toTimestampMs(socketData?.packetReceivedAt ?? socketData?.lastSocketUpdate ?? socketData?.packetTime ?? socketData?.serverReceivedAt ?? socketData?.receivedAt ?? socketData?.timestamp ?? socketData?.serverTime);
    const dbTs = apiLive?.timestamp ? toTimestampMs(apiLive.timestamp) : device?.liveData?.timestamp ? toTimestampMs(device.liveData.timestamp) : toTimestampMs(device?.lastDataReceivedAt) || 0;
    const hasSocketPayload = socketData && Object.keys(socketData).length > 0;
    const socketAgeMs = socketTs > 0 ? Date.now() - socketTs : Infinity;
    const socketFresh = Number.isFinite(socketAgeMs) && socketAgeMs <= 30000;
    const useSocketData = hasSocketPayload && (socketFresh || (socketTs > 0 && socketTs + 20000 >= dbTs));
    const data = useSocketData ? { ...device?.liveData, ...apiLive, ...socketData } : { ...device?.liveData, ...apiLive };
    const snapshot = getResolvedLiveSnapshot(device, useSocketData ? socketData : {});

    const canonicalStatus = getCanonicalVehicleStatus(device, liveInput);
    const status = getBackendSidebarStatus(device, liveInput);

    const lat = Number(snapshot.latitude ?? data?.latitude ?? data?.lat ?? device?.currentLocation?.latitude ?? device?.liveData?.latitude);
    const lng = Number(snapshot.longitude ?? data?.longitude ?? data?.lng ?? device?.currentLocation?.longitude ?? device?.liveData?.longitude);
    const rawSpeed = Number(snapshot.speed ?? data?.speed ?? 0);
    const accSignal = parseAccBool(data?.accStatus ?? data?.acc ?? data?.ignition);
    const currentSpeed = rawSpeed;
    const packetTs = toTimestampMs(
      data?.packetReceivedAt ??
      data?.lastSocketUpdate ??
      data?.packetTime ??
      data?.timestamp ??
      data?.serverTime ??
      data?.lastDataReceivedAt ??
      data?.lastUpdate
    ) || 0;

    const prevTracking = trackingStateByDeviceRef.current.get(deviceKey) || null;
    const tracking = resolveTrackingState({
      prev: prevTracking,
      speed: currentSpeed,
      accStatus: snapshot.accStatus ?? data?.accStatus ?? data?.acc ?? data?.ignition ?? null,
      timestampMs: packetTs,
      latitude: Number.isFinite(lat) ? lat : null,
      longitude: Number.isFinite(lng) ? lng : null,
      heading: data?.heading ?? data?.course ?? data?.bearing ?? 0,
      vehicleStatus: canonicalStatus ?? data?.vehicleStatus ?? data?.vehicle_status ?? data?.status ?? status,
      movementState: data?.movementState ?? data?.movement_state ?? null,
      direction: data?.direction ?? null,
      turnType: data?.turnType ?? data?.turn_type ?? null,
    });
    if (deviceKey) trackingStateByDeviceRef.current.set(deviceKey, tracking);
    const resolvedStatus = status;
    const displaySpeed = resolvedStatus === "running" ? rawSpeed : 0;

    const ignOn = accSignal === null
      ? resolvedStatus === "running" || resolvedStatus === "idle"
      : accSignal === true;

    const freshestTs = Math.max(socketTs || 0, dbTs || 0, packetTs || 0);
    const hasNeverReportedData = resolvedStatus === "no_data" || freshestTs <= 0;
    const lastDate = hasNeverReportedData ? "" : (freshestTs ? formatDate(freshestTs) : formatDate(device?.lastDataReceivedAt));

    const iconFamilyKey = device?.vehicleType ?? device?.deviceIcon ?? device?.vehicleIcon ?? device?.icon ?? "car";
    const cloudinaryIcon = resolveSidebarVehicleIcon(iconFamilyKey, resolvedStatus);
    const defaultIcon = typeof getVehicleIcon === "function" ? getVehicleIcon(iconFamilyKey) : null;
    const iconSrc = cloudinaryIcon || defaultIcon;

    // GPS signal: treat as "on" if we have valid coords
    const gpsOn = Number.isFinite(lat) && lat !== 0 && Number.isFinite(lng) && lng !== 0;
    // PWR: keep green in stopped unless an explicit external power-cut signal says otherwise.
    const explicitPowerState = parsePowerBool(
      data?.power ?? data?.externalPower ?? data?.mainPower ?? data?.powerStatus
    );
    const batteryPowerState =
      data?.battery !== undefined && data?.battery !== null && data?.battery !== ""
        ? Number(data.battery) > 0
        : null;
    const pwrOn =
      explicitPowerState !== null
        ? explicitPowerState
        : batteryPowerState !== null
          ? batteryPowerState
          : resolvedStatus === "stopped"
            ? true
            : ignOn;
    const telemetryIcons = getTelemetryIconState({
      status: resolvedStatus,
      ignOn,
      gpsOn,
      pwrOn,
    });

    const isSelected = selectedDevices.includes(device._id);
    const statusTheme = getSidebarStatusBadgeTheme(resolvedStatus);
    const vehicleName = device?.name || device?.vehicleNumber || "Unnamed";
    const imeiText = device?.imei || device?.uniqueId || "N/A";
    const statusSinceTs = toTimestampMs(
      data?.statusSince ?? data?.statusSinceAt ?? data?.statusChangedAt ?? tracking?.statusSinceMs
    );
    const statusSinceLabel = formatTimeOnly(statusSinceTs || freshestTs || device?.lastDataReceivedAt);

    // Status dot color
    const dotColor = {
      running: "#16a34a", idle: "#ca8a04", stopped: "#dc2626",
      inactive: "#0ea5e9", no_data: "#6b7280", expired: "#7c3aed",
    }[resolvedStatus] || "#6b7280";

    const openDeviceActionsFromContext = (event) => {
      if (!modernTrackingLayout) return;
      openMoreMenu(event, device);
    };

    if (modernTrackingLayout) {
      return (
        <Box
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            setSelectedDevices?.([device._id]);
            onDeviceClick?.(device);
          }}
          onContextMenu={openDeviceActionsFromContext}
          sx={{
            mx: 1.5,
            my: compactMobile ? 0.14 : compactModernSidebar ? 0.16 : 0.18,
            px: compactMobile ? 0.95 : compactModernSidebar ? 1 : 1.15,
            py: compactMobile ? 0.78 : compactModernSidebar ? 0.82 : 0.95,
            border: "1px solid #EEF2F7",
            borderRadius: compactMobile ? "5px" : "6px",
            background: "#FFFFFF",
            cursor: "pointer",
            boxShadow: isSelected ? "0 14px 26px rgba(37,99,235,0.10)" : "0 8px 20px rgba(15,23,42,0.04)",
            transform: "translateY(0)",
            transition: "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
            borderColor: isSelected ? "rgba(37,99,235,0.38)" : "#EEF2F7",
            backgroundColor: isSelected ? "#F8FBFF" : "#FFFFFF",
            "&:hover": {
              boxShadow: "0 14px 28px rgba(15,23,42,0.08)",
              transform: "translateY(-1px)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: compactMobile ? 0.55 : compactModernSidebar ? 0.7 : 0.85 }}>
            <Box sx={{ display: "flex", gap: compactMobile ? 0.65 : 0.9, minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: compactMobile ? 38 : compactModernSidebar ? 40 : 46,
                  height: compactMobile ? 26 : compactModernSidebar ? 28 : 32,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <img
                  src={iconSrc || ""}
                  alt=""
                  onError={(e) => {
                    if (e.currentTarget.dataset.fallbackApplied === "1") return;
                    if (defaultIcon && e.currentTarget.src !== defaultIcon) {
                      e.currentTarget.dataset.fallbackApplied = "1";
                      e.currentTarget.src = defaultIcon;
                      return;
                    }
                    e.currentTarget.dataset.fallbackApplied = "1";
                  }}
                  style={{ width: compactMobile ? 28 : compactModernSidebar ? 30 : 34, height: compactMobile ? 28 : compactModernSidebar ? 30 : 34, objectFit: "contain" }}
                />
              </Box>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: compactModernSidebar ? "13px" : "14px",
                    fontWeight: 800,
                    color: "#0F172A",
                    lineHeight: 1.25,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {vehicleName}
                </Typography>
                <Typography
                  sx={{
                    mt: compactModernSidebar ? 0.2 : 0.35,
                    fontSize: compactModernSidebar ? "10px" : "11px",
                    fontWeight: 500,
                    color: "#64748B",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  IMEI: {imeiText}
                </Typography>
              </Box>
            </Box>

                <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: compactMobile ? 0.5 : 0.7, mt: compactMobile ? 0.25 : compactModernSidebar ? 0.45 : 0.7 }}>
            <Chip
              size="small"
              label={STATUS_PILL_STYLE[resolvedStatus]?.label || resolvedStatus}
              sx={{
                height: compactModernSidebar ? 12 : 22,
                borderRadius: 999,
                backgroundColor: statusTheme.bg,
                color: statusTheme.color,
                fontSize: compactModernSidebar ? "10px" : "11px",
                fontWeight: 800,
                "& .MuiChip-label": { px: compactModernSidebar ? 1 : 1.25 },
              }}
            />
            <Typography sx={{ fontSize: compactModernSidebar ? "10px" : "11px", fontWeight: 600, color: "#64748B", whiteSpace: "nowrap" }}>
              {statusSinceLabel === "—" ? "—" : `Since ${statusSinceLabel}`}
            </Typography>
          </Box>

            <Box sx={{ textAlign: "right", minWidth: compactModernSidebar ? 48 : 62 }}>
              <Typography sx={{ fontSize: compactModernSidebar ? "16px" : "20px", fontWeight: 800, color: "#0F172A", lineHeight: 1 }}>
                {Math.round(displaySpeed)}
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: compactModernSidebar ? "10px" : "11px", fontWeight: 700, color: "#64748B" }}>
                km/h
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: compactMobile ? 0.65 : compactModernSidebar ? 0.8 : 1.1, mt: compactMobile ? 0.28 : compactModernSidebar ? 0.45 : 0.7, flexWrap: "wrap" }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <PlaceOutlinedIcon sx={{ fontSize: compactModernSidebar ? 12 : 14, color: gpsOn ? "#16A34A" : "#94A3B8" }} />
              <Typography sx={{ fontSize: compactModernSidebar ? "7px" : "8px", fontWeight: 600, color: gpsOn ? "#334155" : "#64748B", whiteSpace: "nowrap" }}>
                {getSidebarIndicatorText("gps", gpsOn)}
              </Typography>
            </Box>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <DirectionsCarFilledOutlinedIcon sx={{ fontSize: compactModernSidebar ? 12 : 14, color: ignOn ? "#2563EB" : "#94A3B8" }} />
              <Typography sx={{ fontSize: compactModernSidebar ? "7px" : "8px", fontWeight: 600, color: ignOn ? "#334155" : "#64748B", whiteSpace: "nowrap" }}>
                {getSidebarIndicatorText("ign", ignOn)}
              </Typography>
            </Box>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <BoltIcon sx={{ fontSize: compactModernSidebar ? 12 : 14, color: pwrOn ? "#16A34A" : "#94A3B8" }} />
              <Typography sx={{ fontSize: compactModernSidebar ? "7px" : "8px", fontWeight: 600, color: pwrOn ? "#334155" : "#64748B", whiteSpace: "nowrap" }}>
                {getSidebarIndicatorText("pwr", pwrOn)}
              </Typography>
            </Box>
          </Box>

      
        </Box>
      );
    }

    return (
      <Box
  onMouseDown={(e) => {
    if (e.button !== 0) return;
    setSelectedDevices?.([device._id]);
    onDeviceClick?.(device);
  }}
  sx={{
          display: "grid",
          gridTemplateColumns: desktopRowTemplate,
          alignItems: "center",
          gap: compactDesktop ? "2px" : "4px",
          px: compactDesktop ? 1.1 : 1.3,
          py: compactDesktop ? "7px" : "9px",
          mx: compactDesktop ? 0.5 : 0.75,
          my: 0.45,
          border: "1px solid",
          borderColor: isSelected ? "rgba(37,99,235,0.45)" : "rgba(226,232,240,0.92)",
          borderLeftWidth: isSelected ? "3px" : "1px",
          borderRadius: 2.5,
          cursor: "pointer",
          background: isSelected ? "rgba(37,99,235,0.08)" : "#ffffff",
          boxShadow: isSelected ? "0 10px 24px rgba(37,99,235,0.12)" : "0 6px 18px rgba(15,23,42,0.04)",
          "&:hover": { background: isSelected ? "rgba(37,99,235,0.11)" : "#f8fafc" },
          transition: "background 0.12s, box-shadow 0.12s, border-color 0.12s",
        }}
      >
        {/* Checkbox 1 — select for map */}
        <Checkbox
  size="small"
  checked={isSelected}
  onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => { e.stopPropagation(); toggleSelectDevice(device._id); }}
          sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: compactDesktop ? 14 : 16 } }}
        />

        {/* Checkbox 2 — visibility toggle */}
        <Checkbox
  size="small"
  checked={isSelected}
  onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => { e.stopPropagation(); onToggleVisibility?.(device); }}
          sx={{ p: 0, color: "text.disabled", "& .MuiSvgIcon-root": { fontSize: compactDesktop ? 14 : 16 } }}
          icon={<VisibilityOffIcon sx={{ fontSize: compactDesktop ? 16 : 18 }} />}
          checkedIcon={<VisibilityIcon sx={{ fontSize: compactDesktop ? 16 : 18 }} />}
        />

        {/* Vehicle icon + name + date */}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: compactDesktop ? "5px" : "7px", minWidth: 0 }}>
            <Box sx={{ width: compactDesktop ? 24 : 30, height: compactDesktop ? 18 : 22, flexShrink: 0 }}>
              <img
                src={iconSrc || ""}
                alt=""
                onError={(e) => {
                  if (e.currentTarget.dataset.fallbackApplied === "1") return;
                  if (defaultIcon && e.currentTarget.src !== defaultIcon) {
                    e.currentTarget.dataset.fallbackApplied = "1";
                    e.currentTarget.src = defaultIcon;
                    return;
                  }
                  e.currentTarget.dataset.fallbackApplied = "1";
                }}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box>
            <Typography sx={{ fontSize: compactDesktop ? "12px" : "13px", fontWeight: 700, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
              {device?.name || device?.vehicleNumber || "Unnamed"}
            </Typography>
            <Chip
              size="small"
              label={STATUS_PILL_STYLE[resolvedStatus]?.label || resolvedStatus}
              sx={{
                ml: 0.5,
                height: compactDesktop ? 20 : 22,
                borderRadius: 999,
                bgcolor: `${dotColor}14`,
                color: dotColor,
                fontSize: compactDesktop ? "9px" : "10px",
                fontWeight: 800,
                "& .MuiChip-label": { px: 0.9 },
              }}
            />
          </Box>

          {lastDate ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "3px" }}>
              <Typography sx={{ fontSize: compactDesktop ? "11px" : "12px", fontWeight: 700, color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                {Math.round(displaySpeed)} km/h
              </Typography>
              <Typography sx={{ fontSize: compactDesktop ? "11px" : "12px", color: "#94a3b8" }}>
                •
              </Typography>
              <Typography sx={{ fontSize: compactDesktop ? "11px" : "12px", fontWeight: 700, color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                {lastDate}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: 14 }} />
          )}
        </Box>

        {/* Speed */}
        <Box sx={{ textAlign: "center", visibility: telemetryIcons.hidden ? "hidden" : "visible" }}>
          <Typography sx={{ fontSize: compactDesktop ? "12px" : "14px", fontWeight: 700, color: "text.primary", lineHeight: 1.1 }}>
            {Math.round(displaySpeed)}
          </Typography>
          <Typography sx={{ fontSize: compactDesktop ? "7px" : "8px", color: "text.secondary", lineHeight: 1 }}>kmph</Typography>
        </Box>

        {/* IGN */}
        <IconCell on={telemetryIcons.ignOn} label="IGN" hidden={telemetryIcons.hidden} compact={compactDesktop} />

        {/* GPS */}
        <IconCell on={telemetryIcons.gpsOn} label="GPS" hidden={telemetryIcons.hidden} compact={compactDesktop} />

        {/* PWR */}
        <IconCell on={telemetryIcons.pwrOn} label="PWR" hidden={telemetryIcons.hidden} compact={compactDesktop} />

        {/* More menu */}
       <IconButton
  size="small"
  aria-label="Device actions"
  onMouseDown={(e) => {
    e.preventDefault();
    e.stopPropagation();
  }}
  onPointerDown={(e) => {
    e.stopPropagation();
  }}
  onClick={(e) => openMoreMenu(e, device)}
  sx={{
    width: compactDesktop ? 30 : 34,
    height: compactDesktop ? 30 : 34,
    ml: compactDesktop ? 0.5 : 0.75,
    p: 0,
    color: "text.secondary",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "1px",
    justifySelf: "center",
    borderRadius: "8px",
    zIndex: 2,
    pointerEvents: "auto",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  }}
>
  <MoreVertIcon sx={{ fontSize: compactDesktop ? 17 : 19 }} />
  <ExpandMoreIcon sx={{ fontSize: compactDesktop ? 15 : 17 }} />
</IconButton>
      </Box>
    );
  });

  const renderObjectRow = useCallback(({ index, style }) => {
    const row = objectRows[index];
    if (!row) {
      return (
        <Box style={style} sx={{ display: "grid", placeItems: "center", color: "text.secondary", fontSize: "12px" }}>
          {pagedObjectsLoading ? "Loading devices..." : ""}
        </Box>
      );
    }

    if (row.type === "group") {
      const list = row.devices;
      const isOpen = Boolean(openGroups[row.groupKey]);
      const groupSelected = list.every((device) => selectedDevices.includes(device._id));

      return (
        <Box style={style} key={row.key}>
          <Box
            onClick={() => toggleGroup(row.groupKey)}
            sx={{
              height: "100%",
              display: "grid",
              gridTemplateColumns: desktopRowTemplate,
              alignItems: "center",
              gap: compactDesktop ? "2px" : "4px",
              px: compactDesktop ? 1.1 : 1.5,
              py: compactDesktop ? "5px" : "6px",
              background: "action.hover",
              borderBottom: "0.5px solid",
              borderColor: "divider",
              cursor: "pointer",
              "&:hover": { background: "action.selected" },
            }}
          >
            <Checkbox
              size="small"
              checked={groupSelected}
              indeterminate={list.some((device) => selectedDevices.includes(device._id)) && !groupSelected}
              onClick={(event) => {
                event.stopPropagation();
                if (groupSelected) {
                  setSelectedDevices?.(selectedDevices.filter((id) => !list.find((device) => device._id === id)));
                } else {
                  setSelectedDevices?.([...new Set([...selectedDevices, ...list.map((device) => device._id)])]);
                }
              }}
              sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: compactDesktop ? 14 : 16 } }}
            />
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {isOpen ? <ExpandMoreIcon sx={{ fontSize: compactDesktop ? 14 : 16, color: "text.secondary" }} /> : <ChevronRightIcon sx={{ fontSize: compactDesktop ? 14 : 16, color: "text.secondary" }} />}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: compactDesktop ? "5px" : "7px", minWidth: 0 }}>
              <FolderOutlinedIcon sx={{ fontSize: compactDesktop ? 14 : 16, color: "text.secondary" }} />
              <Typography sx={{ fontSize: compactDesktop ? "12px" : "13px", fontWeight: 800, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.groupName}</Typography>
            </Box>
            <Chip
              label={list.length}
              size="small"
              sx={{ height: 18, fontSize: "10px", fontWeight: 700, background: "#334155", color: "#fff", borderRadius: "10px" }}
            />
            <Box /><Box /><Box />
            <IconButton size="small" onClick={(event) => event.stopPropagation()} sx={{ p: 0, color: "text.secondary" }}>
              {isOpen ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Box>
        </Box>
      );
    }

    return (
      <Box style={style} key={row.key}>
        <DeviceRow device={row.device} liveInput={getLiveStatusInput(row.device)} />
      </Box>
    );
  }, [compactDesktop, desktopRowTemplate, getLiveStatusInput, objectRows, openGroups, pagedObjectsLoading, selectedDevices, setSelectedDevices]);

  // ── Column header row ──────────────────────────────────────────────────────
  const ColHeader = (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: desktopRowTemplate,
      alignItems: "center",
      gap: compactDesktop ? "2px" : "4px",
      px: compactDesktop ? 1.1 : 1.5,
      py: "4px",
      background: "action.hover",
      borderBottom: "0.5px solid",
      borderColor: "divider",
    }}>
      <Checkbox
        size="small"
        checked={isAllSelected}
        indeterminate={selectedDevices.length > 0 && !isAllSelected}
        onChange={toggleSelectAll}
        sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: compactDesktop ? 14 : 16 } }}
      />
      <Box />
      <Typography sx={{ fontSize: compactDesktop ? "10px" : "11px", fontWeight: 700, color: "text.secondary" }}>Object</Typography>
      <Typography sx={{ fontSize: compactDesktop ? "10px" : "11px", fontWeight: 700, color: "text.secondary", textAlign: "center" }}>kmph</Typography>
      <Typography sx={{ fontSize: compactDesktop ? "10px" : "11px", fontWeight: 700, color: "text.secondary", textAlign: "center" }}>IGN</Typography>
      <Typography sx={{ fontSize: compactDesktop ? "10px" : "11px", fontWeight: 700, color: "text.secondary", textAlign: "center" }}>GPS</Typography>
      <Typography sx={{ fontSize: compactDesktop ? "10px" : "11px", fontWeight: 700, color: "text.secondary", textAlign: "center" }}>PWR</Typography>
      <Box sx={{ textAlign: "center" }}>
        <Typography sx={{ fontSize: compactDesktop ? "10px" : "11px", fontWeight: 700, color: "text.secondary" }}>Obj</Typography>
      </Box>
    </Box>
  );

  // ── Header ─────────────────────────────────────────────────────────────────
  const Header = (
    <Box sx={{ position: "sticky", top: 0, zIndex: 3, background: "background.paper", borderBottom: "0.5px solid", borderColor: "divider" }}>
      {/* Offline banner */}
      {!connected && (
        <Box sx={{ px: 1.5, py: 0.75, background: "warning.light", color: "warning.contrastText", display: "flex", alignItems: "center", gap: 1 }}>
          <WifiOffIcon sx={{ fontSize: 14 }} />
          <Typography sx={{ fontSize: "11px", fontWeight: 800 }}>System Offline — Reconnecting...</Typography>
        </Box>
      )}

      {/* Status pills row */}
      {!modernTrackingLayout ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 0.75, pt: 1, pb: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => scrollStatusPills("left")}
            sx={{ p: "3px", border: "1px solid", borderColor: "divider", borderRadius: "8px", flexShrink: 0 }}
          >
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Box
            ref={statusPillsRef}
            sx={{ display: "flex", alignItems: "center", gap: compactMobile ? 0.75 : "5px", flex: 1, overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}
          >
            {STATUS_ORDER.map((s) => {
              const cfg = STATUS_PILL_STYLE[s] || STATUS_PILL_STYLE.no_data;
              const isActive = activeStatus === s;
              return (
                <Box
                  key={s}
                  onClick={() => {
                    setActiveStatus(s);
                    if (s === "all") {
                      setSelectedDevices?.([]);
                    }
                  }}
                  sx={{
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    minWidth: compactMobile ? 62 : 68, px: compactMobile ? 1 : 1.25, py: 0.75,
                    background: cfg.bg,
                    color: cfg.color,
                    borderRadius: "6px",
                    cursor: "pointer",
                    flexShrink: 0,
                    outline: isActive ? "2.5px solid" : "none",
                    outlineColor: isActive ? "text.primary" : "transparent",
                    outlineOffset: "1px",
                    userSelect: "none",
                    transition: "outline 0.1s",
                  }}
                >
                  <Typography sx={{ fontSize: compactMobile ? "16px" : "18px", fontWeight: 700, lineHeight: 1.1, color: "inherit" }}>
                    {statusPillCounts[s] || 0}
                  </Typography>
                  <Typography sx={{ fontSize: compactMobile ? "10px" : "11px", fontWeight: 600, color: "inherit" }}>
                    {cfg.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <IconButton
            size="small"
            onClick={() => scrollStatusPills("right")}
            sx={{ p: "3px", border: "1px solid", borderColor: "divider", borderRadius: "8px", flexShrink: 0 }}
          >
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ) : null}
      {!modernTrackingLayout && progressiveStatusText ? (
        <Box sx={{ px: 1.25, pb: 0.5 }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "text.secondary" }}>
            {progressiveStatusText}
          </Typography>
        </Box>
      ) : null}

      {/* Tabs */}
      {!modernTrackingLayout ? (
      <Box sx={{ display: "flex", gap: compactMobile ? 0.5 : 0, px: compactMobile ? 1 : 0, py: compactMobile ? 0.75 : 0, borderBottom: "0.5px solid", borderColor: "divider", overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
        {TABS.map((tab) => (
          <Box
            key={tab}
            onClick={() => setActiveTab(tab)}
            sx={{
              px: compactMobile ? 1.25 : 1.5,
              py: compactMobile ? "8px" : "7px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontSize: compactMobile ? "11px" : "12px",
              fontWeight: activeTab === tab ? 700 : 400,
              color: activeTab === tab ? "text.primary" : "text.secondary",
              borderBottom: compactMobile ? "none" : "2px solid",
              borderColor: activeTab === tab ? "text.primary" : "transparent",
              borderRadius: compactMobile ? "999px" : 0,
              background: compactMobile && activeTab === tab ? "rgba(15,23,42,0.08)" : "transparent",
              transition: "all 0.12s",
            }}
          >
            {tab}
          </Box>
        ))}
      </Box>
      ) : null}

      {modernTrackingLayout && activeTab === "Objects" ? (
        <Box
          sx={{
            px: 1.5,
            pt: compactModernSidebar ? 1.15 : 1.5,
            pb: compactModernSidebar ? 0.35 : 0.5,
            background: "#fff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontSize: compactModernSidebar ? "15px" : "17px", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
              Vehicles{" "}
              <Box component="span" sx={{ fontSize: compactModernSidebar ? "13px" : "15px", color: "#64748b", fontWeight: 700 }}>
                ({visibleFilteredDevices.length})
              </Box>
            </Typography>
            <Tooltip title={sidebarOpen ? "Collapse" : "Expand"}>
              <IconButton
                size="small"
                sx={{ p: compactModernSidebar ? "7px" : "9px", border: "1px solid #dbe6f1", borderRadius: "14px" }}
                onClick={() => setSidebarOpen((s) => !s)}
              >
                {isMobile ? <CloseIcon sx={{ fontSize: 16 }} /> : sidebarOpen ? <ChevronLeftIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ) : null}

      {/* Toolbar: Device label + search + icons */}
      {activeTab !== "History" && (
      <Box sx={{ display: "flex", alignItems: "center", gap: compactModernSidebar ? 0.55 : 0.75, flexWrap: compactMobile || compactModernSidebar ? "wrap" : "nowrap", px: modernTrackingLayout ? 1.5 : 1.25, py: modernTrackingLayout ? (compactModernSidebar ? 1.1 : 1.5) : 1, bgcolor: "#fff", borderBottom: modernTrackingLayout ? "1px solid #e8eef6" : "none" }}>
        <Typography sx={{ fontWeight: 800, fontSize: compactMobile ? "13px" : "14px", color: "text.primary", mr: 0.5, minWidth: compactMobile ? "100%" : "auto", display: modernTrackingLayout ? "none" : "block" }}>
          {activeTab === "Geofence" ? "Geofence" : activeTab === "Notifications" ? "Updates" : "Vehicles"}
        </Typography>
        <TextField
          value={searchTerm}
          onChange={(e) => setSearchTerm?.(e.target.value)}
          placeholder={activeTab === "Geofence" ? "Search Geofence" : activeTab === "Notifications" ? "Search Vehicle" : modernTrackingLayout ? "Search vehicles..." : "Search by vehicle number / device ID"}
          size="small"
          sx={{
            flex: modernTrackingLayout ? "1 1 118px" : 1,
            minWidth: compactMobile ? "100%" : modernTrackingLayout ? 0 : 0,
            "& .MuiOutlinedInput-root": {
              borderRadius: modernTrackingLayout ? 3.5 : 2.5,
              bgcolor: modernTrackingLayout ? "#ffffff" : "#fff",
              minHeight: modernTrackingLayout ? (compactModernSidebar ? 42 : 48) : "auto",
              border: modernTrackingLayout ? "1px solid #dbe6f1" : undefined,
            },
            "& .MuiInputBase-input": { fontSize: modernTrackingLayout ? (compactModernSidebar ? "12px" : "13px") : "12px", py: modernTrackingLayout ? (compactModernSidebar ? "8px" : "11px") : "9px" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: modernTrackingLayout ? (compactModernSidebar ? 16 : 18) : 15, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
        {activeTab === "Objects" ? (
          <Select
            size="small"
            value={activeStatus}
            onChange={(e) => setActiveStatus(e.target.value)}
            sx={{
              minWidth: compactMobile ? "100%" : compactModernSidebar ? 94 : 118,
              borderRadius: modernTrackingLayout ? 3.5 : 2.5,
              bgcolor: modernTrackingLayout ? "#ffffff" : "#fff",
              minHeight: modernTrackingLayout ? (compactModernSidebar ? 42 : 48) : "auto",
              border: modernTrackingLayout ? "1px solid #dbe6f1" : undefined,
              "& .MuiSelect-select": { fontSize: modernTrackingLayout ? (compactModernSidebar ? "12px" : "13px") : "12px", py: modernTrackingLayout ? (compactModernSidebar ? "8px" : "11px") : "9px" },
            }}
          >
            {STATUS_ORDER.map((status) => (
              <MenuItem key={status} value={status}>
                {STATUS_PILL_STYLE[status]?.label || status}
              </MenuItem>
            ))}
          </Select>
        ) : null}
        {activeTab === "Notifications" ? (
          <TextField
            select
            value={notificationEventFilter}
            onChange={(e) => setNotificationEventFilter(e.target.value)}
            size="small"
            sx={{
              minWidth: compactMobile ? "100%" : 132,
              "& .MuiInputBase-input": { fontSize: "12px", py: "5px" },
            }}
            SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
          >
            <MenuItem value="all">Event Filter</MenuItem>
            {notificationItems.eventOptions.map((eventName) => (
              <MenuItem key={eventName} value={eventName}>
                {prettyNotificationEvent(eventName)}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        <Tooltip title="Refresh">
          <IconButton size="small" sx={{ p: modernTrackingLayout ? (compactModernSidebar ? "8px" : "10px") : "4px", border: modernTrackingLayout ? "1px solid #dbe6f1" : "none", borderRadius: modernTrackingLayout ? "14px" : undefined }} onClick={handleRefreshClick}><RefreshIcon sx={{ fontSize: modernTrackingLayout ? (compactModernSidebar ? 16 : 18) : 16 }} /></IconButton>
        </Tooltip>
        {!modernTrackingLayout ? (
        <Tooltip title="List view">
          <IconButton size="small" sx={{ p: "4px" }} onClick={() => openQuickModal("devices")}><ViewListIcon sx={{ fontSize: 16 }} /></IconButton>
        </Tooltip>
        ) : null}
        {activeTab === "Objects" ? (
          !modernTrackingLayout ? (
          <Tooltip title="Add device">
            <IconButton size="small" sx={{ p: "4px" }} onClick={onAddDevice}>
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          ) : null
        ) : activeTab === "Geofence" && onCreateSidebarGeofence ? (
          <Tooltip title="Add geofence">
            <IconButton size="small" sx={{ p: "4px" }} onClick={(event) => setGeofenceMenuAnchor(event.currentTarget)}>
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : null}
        {!modernTrackingLayout ? (
          <Tooltip title={sidebarOpen ? "Collapse" : "Expand"}>
            <IconButton size="small" sx={{ p: modernTrackingLayout ? "10px" : "4px", border: modernTrackingLayout ? "1px solid #dbe6f1" : "none", borderRadius: modernTrackingLayout ? "14px" : undefined }} onClick={() => setSidebarOpen((s) => !s)}>
              {isMobile ? <CloseIcon sx={{ fontSize: 16 }} /> : sidebarOpen ? <ChevronLeftIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      )}

      {/* Column headers */}
      {activeTab === "Objects" && !modernTrackingLayout ? ColHeader : null}
      {activeTab === "Notifications" ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1.1fr 1.3fr 1fr 32px",
            gap: 1,
            px: 1.5,
            py: 0.7,
            background: "#f8fafc",
            borderTop: "1px solid #eef2f7",
            borderBottom: "1px solid #eef2f7",
          }}
        >
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "text.secondary" }}>Object</Typography>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "text.secondary" }}>Time</Typography>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "text.secondary" }}>Event</Typography>
          <Box />
        </Box>
      ) : null}
    </Box>
  );

  // ── Body ───────────────────────────────────────────────────────────────────
  const Body = (
    <Box
      ref={scrollRef}
      sx={{
        flex: 1,
        overflowY: activeTab === "Objects" ? "hidden" : "auto",
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "contain",
      }}
    >
      {activeTab === "History" ? (
        <Box sx={{ p: 0.75, background: "#fff" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "72px minmax(0,1fr)", gap: 1, alignItems: "center" }}>
            <Typography sx={{ fontSize: "11px", color: "text.primary" }}>Reg No</Typography>
            <Box ref={historyDeviceDropdownRef} sx={{ position: "relative" }}>
              <TextField
                fullWidth
                value={historyDeviceQuery}
                onChange={(e) => {
                  setHistoryDeviceQuery(e.target.value);
                  setHistoryDeviceDropdownOpen(true);
                }}
                onFocus={() => setHistoryDeviceDropdownOpen(true)}
                placeholder="Search for an object"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: "#fff" },
                  "& .MuiInputBase-input": { fontSize: "12px", py: "8px" },
                }}
              />
              {historyDeviceDropdownOpen && historyDeviceOptions.length > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "calc(100% + 2px)",
                    left: 0,
                    right: 0,
                    height: 208,
                    overflowY: "auto",
                    bgcolor: "#fff",
                    border: "1px solid #dbe3ef",
                    boxShadow: "0 14px 28px rgba(15,23,42,0.14)",
                    borderRadius: "0 0 14px 14px",
                    zIndex: 5,
                    pt: 0.4,
                    pb: 0.4,
                  }}
                >
                  {historyDeviceOptions.map((device) => (
                    <Box
                      key={device?._id}
                      onClick={() => {
                        setHistoryDeviceId(device?._id);
                        setHistoryDeviceQuery(device?.name || device?.vehicleNumber || device?.imei || "");
                        setHistoryDeviceDropdownOpen(false);
                      }}
                      sx={{
                        mx: 0.5,
                        px: 1.6,
                        py: 1.1,
                        fontSize: "12px",
                        cursor: "pointer",
                        borderRadius: 1.5,
                        color: String(historyDeviceId) === String(device?._id) ? "#0f172a" : "#1f2937",
                        fontWeight: String(historyDeviceId) === String(device?._id) ? 700 : 500,
                        background: String(historyDeviceId) === String(device?._id) ? "#eef4ff" : "transparent",
                        border: "1px solid transparent",
                        borderColor: String(historyDeviceId) === String(device?._id) ? "#bfdbfe" : "transparent",
                        "&:hover": { background: "#f8fafc" },
                      }}
                    >
                      {device?.name || device?.vehicleNumber || device?.imei}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Typography sx={{ fontSize: "11px", color: "text.primary" }}>Filter</Typography>
            <Box
              onClick={() => setHistoryFilter((prev) => prev === "Today" ? "Custom" : "Today")}
              sx={{
                minHeight: 38,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                border: "1px solid #dbe3ef",
                borderRadius: 1.5,
                bgcolor: "#fff",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              <span>{historyFilter}</span>
              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#fb7185" }} />
            </Box>

            <Typography sx={{ fontSize: "11px", color: "text.primary" }}>From</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 48px 18px 48px 38px", gap: 0.5, alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", border: "1px solid #dbe3ef", borderRadius: 1.5, overflow: "hidden", bgcolor: "#fff" }}>
                <Box sx={{ px: 1, py: 0.9, background: "#eef2f7", display: "grid", placeItems: "center" }}>
                  <CalendarMonthIcon sx={{ fontSize: 16, color: "#374151" }} />
                </Box>
                <TextField
                  type="date"
                  value={historyFromDate}
                  onChange={(e) => setHistoryFromDate(e.target.value)}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{ flex: 1, "& .MuiInputBase-input": { fontSize: "12px", px: 1.25, py: 1 } }}
                />
              </Box>
              <TextField select value={historyFromHour} onChange={(e) => setHistoryFromHour(e.target.value)} size="small" sx={{ "& .MuiInputBase-input": { fontSize: "12px", py: "8px", textAlign: "center" } }}>
                {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((hour) => <MenuItem key={hour} value={hour}>{hour}</MenuItem>)}
              </TextField>
              <Typography sx={{ textAlign: "center", fontWeight: 700 }}>:</Typography>
              <TextField select value={historyFromMinute} onChange={(e) => setHistoryFromMinute(e.target.value)} size="small" sx={{ "& .MuiInputBase-input": { fontSize: "12px", py: "8px", textAlign: "center" } }}>
                {["00", "15", "30", "45", "59"].map((minute) => <MenuItem key={minute} value={minute}>{minute}</MenuItem>)}
              </TextField>
              <TextField select value={historyFromMeridiem} onChange={(e) => setHistoryFromMeridiem(e.target.value)} size="small" sx={{ "& .MuiInputBase-input": { fontSize: "12px", py: "8px", textAlign: "center" } }}>
                {["AM", "PM"].map((part) => <MenuItem key={part} value={part}>{part}</MenuItem>)}
              </TextField>
            </Box>

            <Typography sx={{ fontSize: "11px", color: "text.primary" }}>To</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 48px 18px 48px 38px", gap: 0.5, alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", border: "1px solid #dbe3ef", borderRadius: 1.5, overflow: "hidden", bgcolor: "#fff" }}>
                <Box sx={{ px: 1, py: 0.9, background: "#eef2f7", display: "grid", placeItems: "center" }}>
                  <CalendarMonthIcon sx={{ fontSize: 16, color: "#374151" }} />
                </Box>
                <TextField
                  type="date"
                  value={historyToDate}
                  onChange={(e) => setHistoryToDate(e.target.value)}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{ flex: 1, "& .MuiInputBase-input": { fontSize: "12px", px: 1.25, py: 1 } }}
                />
              </Box>
              <TextField select value={historyToHour} onChange={(e) => setHistoryToHour(e.target.value)} size="small" sx={{ "& .MuiInputBase-input": { fontSize: "12px", py: "8px", textAlign: "center" } }}>
                {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((hour) => <MenuItem key={hour} value={hour}>{hour}</MenuItem>)}
              </TextField>
              <Typography sx={{ textAlign: "center", fontWeight: 700 }}>:</Typography>
              <TextField select value={historyToMinute} onChange={(e) => setHistoryToMinute(e.target.value)} size="small" sx={{ "& .MuiInputBase-input": { fontSize: "12px", py: "8px", textAlign: "center" } }}>
                {["00", "15", "30", "45", "59"].map((minute) => <MenuItem key={minute} value={minute}>{minute}</MenuItem>)}
              </TextField>
              <TextField select value={historyToMeridiem} onChange={(e) => setHistoryToMeridiem(e.target.value)} size="small" sx={{ "& .MuiInputBase-input": { fontSize: "12px", py: "8px", textAlign: "center" } }}>
                {["AM", "PM"].map((part) => <MenuItem key={part} value={part}>{part}</MenuItem>)}
              </TextField>
            </Box>

            <Typography sx={{ fontSize: "11px", color: "text.primary" }}>Stops</Typography>
            <Box
              sx={{
                minHeight: 38,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                border: "1px solid #dbe3ef",
                borderRadius: 1.5,
                bgcolor: "#fff",
                fontSize: "12px",
              }}
            >
              <span>All</span>
              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#fb7185" }} />
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.35fr", gap: 1.2, mt: 1.25 }}>
            <Box onClick={fetchHistoryReport} sx={{ py: 1, textAlign: "center", borderRadius: 1.25, bgcolor: "#f8fafc", border: "1px solid #eef2f7", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>Show</Box>
            <Box
              onClick={() => {
                setHistoryVisible(false);
                setHistoryError("");
                setHistoryResults([]);
                lastAutoPlaybackKeyRef.current = "";
                onClosePlayback?.();
              }}
              sx={{ py: 1, textAlign: "center", borderRadius: 1.25, bgcolor: "#f8fafc", border: "1px solid #eef2f7", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}
            >
              Hide
            </Box>
            <Box onClick={(e) => setHistoryExportAnchor(e.currentTarget)} sx={{ py: 1, textAlign: "center", borderRadius: 1.25, bgcolor: "#f8fafc", border: "1px solid #eef2f7", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>Import/Export</Box>
          </Box>

          {historyError ? (
            <Box sx={{ mt: 1, px: 0.75, color: "#dc2626", fontSize: "11px", fontWeight: 600 }}>{historyError}</Box>
          ) : null}

          {historyLoading ? (
            <Box sx={{ p: 2, textAlign: "center", color: "text.secondary", fontSize: "12px" }}>Loading history...</Box>
          ) : null}

          {historyVisible && historyResults.length ? (
            <Box sx={{ mt: 1.2, borderTop: "1px solid #eef2f7", pt: 1 }}>
              {historyResults.map((item) => (
                <Box
                  key={item.id}
                  onClick={async () => {
                    if (!historyDeviceId || typeof onOpenPlayback !== "function") return;
                    try {
                      const token = localStorage.getItem("token");
                      const apiBase =
                        process.env.REACT_APP_BACKEND_URL ||
                        process.env.REACT_APP_API_URL ||
                        "https://app.krush.in/api";
                      const startTs = new Date(item.startTimeRaw).toISOString();
                      const endTs = new Date(item.endTimeRaw).toISOString();
                      const pbResp = await fetch(
                        `${apiBase}/history/${historyDeviceId}/playback?startDate=${encodeURIComponent(historyFromDate)}&endDate=${encodeURIComponent(historyToDate)}&startTs=${encodeURIComponent(startTs)}&endTs=${encodeURIComponent(endTs)}`,
                        {
                          headers: {
                            Authorization: token ? `Bearer ${token}` : "",
                            "x-auth-token": token || "",
                            "Content-Type": "application/json",
                          },
                        }
                      );
                      if (!pbResp.ok) return;
                      const pb = await pbResp.json();
                      const points = Array.isArray(pb?.points) ? pb.points : [];
                      if (!points.length) return;
                      onOpenPlayback({
                        id: `history_${historyDeviceId}_${item.id}`,
                        deviceId: historyDeviceId,
                        date: item?.startLabel || historyFromDate,
                        route: points,
                        distance: Number(pb?.stats?.totalDistanceRawKm ?? 0),
                        durationSeconds: Number(pb?.stats?.movingTime ?? 0) + Number(pb?.stats?.idleTime ?? 0),
                        startTime: item.startTimeRaw,
                        endTime: item.endTimeRaw,
                      });
                    } catch {
                      // noop
                    }
                  }}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "112px 44px 1fr 78px",
                    gap: 1,
                    alignItems: "start",
                    px: 0.9,
                    py: 1,
                    borderBottom: "1px solid #eef2f7",
                    background: "#fff",
                    cursor: typeof onOpenPlayback === "function" ? "pointer" : "default",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: "11px", fontWeight: 800, color: "#1f2937" }}>{item.startLabel}</Typography>
                    <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <PlaceOutlinedIcon sx={{ fontSize: 16, color: "#1f9d55" }} />
                      <Typography sx={{ fontSize: "10.5px", color: "#1f2937", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {item.startAddress}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "grid", placeItems: "center", pt: 0.25 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        border: `2px solid ${item.isRide ? "#1d4fff" : "#ff2d20"}`,
                        color: item.isRide ? "#1d4fff" : "#ff2d20",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      {item.isRide ? <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 14 }} /> : <Typography sx={{ fontSize: "11px", fontWeight: 800 }}>P</Typography>}
                    </Box>
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#1f2937" }}>{item.durationText}</Typography>
                    <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <PlaceOutlinedIcon sx={{ fontSize: 16, color: "#e43c4f" }} />
                      <Typography sx={{ fontSize: "10.5px", color: "#1f2937", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {item.endAddress}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ textAlign: "right", pt: 0.2 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#1f2937" }}>{item.distanceText}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
      ) : activeTab === "Notifications" ? (
        <Box sx={{ background: "#fff" }}>
          {notificationItems.rows
            .filter((alarm) => notificationEventFilter === "all" || String(alarm?.alarmType || "") === String(notificationEventFilter))
            .map((alarm) => {
              const isSelected = String(selectedNotificationId || "") === String(alarm?._id || alarm?.alarmId || "");
              const hasCoords = Boolean(getNotificationCoords(alarm));
              return (
                <Box
                  key={alarm?._id || alarm?.alarmId}
                  onClick={() => hasCoords && onSelectNotification?.(alarm)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.1fr 1.3fr 1fr 32px",
                    gap: 1,
                    px: 1.5,
                    py: 1.05,
                    alignItems: "center",
                    borderBottom: "1px solid #eef2f7",
                    background: isSelected ? "#f8fbff" : "#fff",
                    cursor: hasCoords ? "pointer" : "default",
                    "&:hover": {
                      background: hasCoords ? "#f8fafc" : "#fff",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: "12px", color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {alarm?.deviceName || alarm?.imei || "Unknown Device"}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {formatNotificationTime(alarm?.triggeredAt || alarm?.timestamp)}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {prettyNotificationEvent(alarm?.alarmType || alarm?.eventType)}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                    <IconButton
                      size="small"
                      onClick={(e) => openNotificationMenu(e, alarm)}
                      sx={{ p: "2px" }}
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          {!notificationItems.rows.filter((alarm) => notificationEventFilter === "all" || String(alarm?.alarmType || "") === String(notificationEventFilter)).length ? (
            <Box sx={{ p: 2, color: "text.secondary", fontSize: "12px" }}>
              No notifications found.
            </Box>
          ) : null}
        </Box>
      ) : activeTab === "Geofence" ? (
        geofenceItems.length ? (
          <Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 0.75 }}>
            {geofenceItems.map((item) => {
              const isSelected = String(selectedGeofenceId || "") === item.id;
              return (
                <Box
                  key={item.id}
                  onClick={() => onSelectGeofence?.(item.raw || item)}
                  sx={{
                    px: 1,
                    py: 0.95,
                    borderRadius: 1.5,
                    border: "1px solid #e5e7eb",
                    background: isSelected ? "rgba(37, 99, 235, 0.08)" : "#fff",
                    cursor: "pointer",
                    display: "grid",
                    gridTemplateColumns: "38px minmax(0, 1fr) 84px 86px",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": {
                      borderColor: "primary.main",
                      background: "rgba(37, 99, 235, 0.05)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: item.type === "circle" ? "999px" : "8px",
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      background: "#fff",
                      border: "2px solid",
                      borderColor: isSelected ? "primary.main" : "#9ca3af",
                    }}
                  />

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 800, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.name}
                    </Typography>
                    <Typography sx={{ mt: 0.2, fontSize: "11px", color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textTransform: "capitalize" }}>
                      {item.category || item.type}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "text.primary" }}>
                      {item.radiusText || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", color: "text.secondary" }}>
                      {item.radiusText ? "Radius" : `${item.assignedCount || 0} Vehicle`}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.25 }}>
                    {onEditSidebarGeofence ? (
                      <IconButton
                        size="small"
                        sx={{ p: "2px", color: "#f43f5e" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          onEditSidebarGeofence(item.raw || item);
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : null}
                    {onDeleteSidebarGeofence ? (
                      <IconButton
                        size="small"
                        sx={{ p: "2px", color: "#f43f5e" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          onDeleteSidebarGeofence(item.raw || item);
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : null}
                    {onAssignSidebarGeofence ? (
                      <IconButton
                        size="small"
                        sx={{ p: "2px", color: "#2563eb" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          onAssignSidebarGeofence(item.raw || item);
                        }}
                      >
                        <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ p: 2, color: "text.secondary", fontSize: "13px" }}>
            No geofence found.
          </Box>
        )
      ) : (
        objectRows.length ? (
          <Box sx={{ height: "100%" }}>
            <InfiniteLoader
              isItemLoaded={(index) => index < objectRows.length}
              itemCount={objectRows.length + (pagedObjectsHasMore ? 1 : 0)}
              loadMoreItems={async () => {
                if (!pagedObjectsHasMore || pagedObjectsLoading) return;
                await fetchObjectDevicesPage({ page: pagedObjectsPage + 1 });
              }}
            >
              {({ onItemsRendered, ref }) => (
                <FixedSizeList
                  key={`${activeStatus}:${searchTerm}:${activeTab}`}
                  ref={ref}
                  height={objectsListHeight}
                  width="100%"
                  itemCount={objectRows.length + (pagedObjectsHasMore ? 1 : 0)}
                  itemSize={modernTrackingLayout ? (compactModernSidebar ? 78 : 84) : compactDesktop ? 68 : 80}
                  overscanCount={10}
                  onItemsRendered={({ overscanStartIndex, overscanStopIndex, visibleStartIndex, visibleStopIndex }) =>
                    onItemsRendered({
                      overscanStartIndex,
                      overscanStopIndex,
                      visibleStartIndex,
                      visibleStopIndex,
                    })
                  }
                >
                  {renderObjectRow}
                </FixedSizeList>
              )}
            </InfiniteLoader>
          </Box>
        ) : (
          <Box sx={{ p: 2, color: "text.secondary", fontSize: "13px" }}>
            No devices found.
          </Box>
        )
      )}
    </Box>
  );

  // ── Footer ─────────────────────────────────────────────────────────────────
  const Footer = (
    <Box sx={{ px: 1.5, py: 1, borderTop: "0.5px solid", borderColor: "divider", display: modernTrackingLayout ? "none" : "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: connected ? "success.main" : "error.main" }} />
      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "text.secondary" }}>
        {connected ? "Connected" : "Disconnected"}
      </Typography>
    </Box>
  );

  // ── Context Menu ───────────────────────────────────────────────────────────
  const MoreMenu = (
    <>
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={menuPosition || undefined}
        anchorEl={menuAnchor}
        open={Boolean(menuPosition)}
        onClose={closeMoreMenu}
        transformOrigin={{ vertical: "center", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 240,
            borderRadius: 2,
            background: "#eef4fb",
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.18)",
            border: "1px solid rgba(148, 163, 184, 0.22)",
            overflow: "hidden",
            "& .MuiMenuItem-root": {
              py: 1.5,
              px: 2,
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "#1f2937",
              gap: 1,
            },
            "& .MuiListItemIcon-root": {
              minWidth: 34,
              color: "#1f2937",
            },
          },
        }}
      >
        <MenuItem onClick={() => { const d = menuDevice; closeMoreMenu(); if (d) onEditDevice?.(d); }}>
          <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        <MenuItem onClick={() => { const d = menuDevice; closeMoreMenu(); if (d) handleOpenCommandModal?.(d, { uiMode: "simple", commandScope: "full" }); }}>
          <ListItemIcon><BoltIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Send GPRS Command" />
        </MenuItem>
        <MenuItem onClick={() => { const d = menuDevice; closeMoreMenu(); if (d) openShareDialog(d); }}>
          <ListItemIcon><ShareOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Share location" />
        </MenuItem>
        <MenuItem onClick={() => { const d = menuDevice; closeMoreMenu(); if (d) openDeviceHistoryPanel(d); }}>
          <ListItemIcon><HistoryOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Show history" />
        </MenuItem>
        {onOpenGeofence && (
          <MenuItem onClick={() => { const d = menuDevice; closeMoreMenu(); if (d) onOpenGeofence?.(d); }}>
            <ListItemIcon><InfoOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Geofence" />
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={historyExportAnchor}
        open={Boolean(historyExportAnchor)}
        onClose={closeHistoryExportMenu}
        PaperProps={{
          sx: {
            minWidth: 190,
            borderRadius: 2,
            "& .MuiMenuItem-root": {
              fontSize: "13px",
              fontWeight: 600,
              gap: 1,
            },
          },
        }}
      >
        <MenuItem onClick={() => exportHistoryData("csv")}>
          <ListItemIcon><FileDownloadOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Export CSV" />
        </MenuItem>
        <MenuItem onClick={() => exportHistoryData("json")}>
          <ListItemIcon><FileDownloadOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Export JSON" />
        </MenuItem>
        <MenuItem onClick={() => historyImportInputRef.current?.click()}>
          <ListItemIcon><FileUploadOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Import File" />
        </MenuItem>
      </Menu>

      <Menu
        anchorReference="anchorPosition"
        anchorPosition={notificationMenuPosition || undefined}
        anchorEl={notificationMenuAnchor}
        open={Boolean(notificationMenuPosition)}
        onClose={closeNotificationMenu}
        transformOrigin={{ vertical: "center", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 190,
            borderRadius: 2,
            background: "#eef4fb",
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.18)",
            border: "1px solid rgba(148, 163, 184, 0.22)",
            "& .MuiMenuItem-root": {
              py: 1.3,
              px: 2,
              fontSize: "0.92rem",
              fontWeight: 600,
              gap: 1,
            },
          },
        }}
      >
        <MenuItem onClick={openRemarkDialog}>
          <ListItemIcon><NoteAltOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Add Remark" />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={geofenceMenuAnchor}
        open={Boolean(geofenceMenuAnchor)}
        onClose={() => setGeofenceMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 220,
            borderRadius: 2,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setGeofenceMenuAnchor(null);
            onCreateSidebarGeofence?.("polygon");
          }}
        >
          Polygon geofence
        </MenuItem>
        <MenuItem
          onClick={() => {
            setGeofenceMenuAnchor(null);
            onCreateSidebarGeofence?.("circle");
          }}
        >
          Circular geofence
        </MenuItem>
      </Menu>

      <input
        ref={historyImportInputRef}
        type="file"
        accept=".json,.csv"
        style={{ display: "none" }}
        onChange={handleImportHistoryFile}
      />

      <Dialog open={shareDialogOpen} onClose={closeShareDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          Share Location
          <IconButton onClick={closeShareDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 14, color: "#4b5563", mb: 1 }}>
            {shareDevice?.name || shareDevice?.vehicleNumber || shareDevice?.imei || "Vehicle"}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
            <Box sx={{ minWidth: { xs: "100%", sm: 180 } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75, color: "#111827" }}>
                Valid For
              </Typography>
              <Select
                fullWidth
                size="small"
                value={sharePreset}
                onChange={(event) => setSharePreset(event.target.value)}
              >
                {SHARE_PRESETS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {sharePreset === "custom" ? (
              <TextField
                label="Hours"
                type="number"
                size="small"
                value={shareCustomHours}
                onChange={(event) => setShareCustomHours(event.target.value)}
                inputProps={{ min: 1, max: 720 }}
                sx={{ width: { xs: "100%", sm: 130 } }}
              />
            ) : null}
            <Button
              variant="contained"
              onClick={handleRegenerateShareLink}
              disabled={shareLoading}
              sx={{ minWidth: 140, height: 40, alignSelf: { xs: "stretch", sm: "flex-end" } }}
            >
              {shareLoading ? "Generating..." : "Generate Link"}
            </Button>
          </Stack>

          <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e5e7eb" }}>
            {shareLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, color: "#475569" }}>
                <CircularProgress size={18} />
                <Typography sx={{ fontWeight: 600 }}>Creating public tracking link...</Typography>
              </Box>
            ) : (
              <>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 0.75 }}>
                  Public Link
                </Typography>
                <Typography sx={{ fontSize: 14, color: shareLink ? "#111827" : "#9ca3af", wordBreak: "break-all" }}>
                  {shareLink || "Generate link to share live location."}
                </Typography>
                {shareExpiresAt ? (
                  <Typography sx={{ fontSize: 12, color: "#64748b", mt: 1 }}>
                    Valid till: {new Date(shareExpiresAt).toLocaleString()}
                  </Typography>
                ) : null}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button startIcon={<ContentCopyIcon />} onClick={handleCopyShareLink} disabled={!shareLink || shareLoading}>
              Copy
            </Button>
            <Button startIcon={<OpenInNewIcon />} onClick={handleOpenShareLink} disabled={!shareLink || shareLoading}>
              Open
            </Button>
            <Button startIcon={<WhatsAppIcon />} onClick={handleWhatsAppShare} disabled={!shareLink || shareLoading}>
              WhatsApp
            </Button>
          </Stack>
          <Button onClick={closeShareDialog} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={remarkDialogOpen}
        onClose={() => {
          setRemarkDialogOpen(false);
          setRemarkAlarm(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1rem" }}>
          Add Remark
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
            placeholder="Write remark here"
            sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => {
              setRemarkDialogOpen(false);
              setRemarkAlarm(null);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRemark}
            disabled={savingRemark}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return (
    <Box
      className="ds-container"
      sx={{
        width: { xs: "100%", md: DRAWER_WIDTH },
        minWidth: { xs: "100%", md: DRAWER_WIDTH },
        maxWidth: { xs: "100%", md: DRAWER_WIDTH },
        height: compactMobile ? `calc(100dvh - ${MOBILE_HEADER_OFFSET + 18}px)` : "100%",
        minHeight: 0,
        background: "#fff",
        borderRight: "0.5px solid",
        borderColor: "divider",
        overflow: "hidden",
        position: "relative",
        borderRadius: compactMobile ? "18px" : 0,
        boxShadow: compactMobile ? "0 16px 32px rgba(15,23,42,0.18)" : "4px 0 15px rgba(0,0,0,0.03)",
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {Header}
        {Body}
        {Footer}
      </Box>
      {MoreMenu}

    </Box>
  );
}
